/** Bootstraps Mistsweeper in the browser: settings, theme, renderer, controller, input, loop. */
import "@fontsource-variable/archivo/wdth.css";
import { ATLAS, type SoundId, THEMES, type ThemeDef } from "@mistsweeper/assets";
import atlasUrl from "@mistsweeper/assets/atlas/atlas.png";
import worldGlbUrl from "@mistsweeper/assets/models/world.glb?url";
import bergstrollUrl from "@mistsweeper/assets/portraits/bergstroll.png?url";
import huldraUrl from "@mistsweeper/assets/portraits/huldra.png?url";
import jatteUrl from "@mistsweeper/assets/portraits/jatte.png?url";
import nackenUrl from "@mistsweeper/assets/portraits/nacken.png?url";
import bergstrollNightUrl from "@mistsweeper/assets/portraits/night/bergstroll.png?url";
import huldraNightUrl from "@mistsweeper/assets/portraits/night/huldra.png?url";
import jatteNightUrl from "@mistsweeper/assets/portraits/night/jatte.png?url";
import nackenNightUrl from "@mistsweeper/assets/portraits/night/nacken.png?url";
import sjotrollNightUrl from "@mistsweeper/assets/portraits/night/sjotroll.png?url";
import skogstrollNightUrl from "@mistsweeper/assets/portraits/night/skogstroll.png?url";
import sjotrollUrl from "@mistsweeper/assets/portraits/sjotroll.png?url";
import skogstrollUrl from "@mistsweeper/assets/portraits/skogstroll.png?url";
import {
  type Coord,
  dayNumber,
  expeditionNumber,
  formatDistance,
  Game,
  LESSON_STEPS,
  type LessonStep,
  type Mode,
  msUntilNextDay,
  parseRunRecord,
  type RunRecord,
  shareMap,
  Terrain,
} from "@mistsweeper/core";
import {
  type CameraView,
  creatureKind,
  creatureOn,
  creatureSaying,
  type Dress,
  FADE_MS,
  FLAG,
  loadModels,
  MIN_SCALE,
  type Pick as ModelPick,
  type ModelSet,
  makePick,
  sayingOf,
  WorldRenderer,
  type WorldSource,
} from "@mistsweeper/renderer";
import { nameFits, normaliseName } from "../worker/names.ts";
import {
  BIRDS_LEAD_S,
  bedLevels,
  callLevel,
  duckCalls,
  eagleCries,
  elkGrunts,
  FISH_SPLASH_S,
  fishFalls,
  gnatLevel,
  homeLevel,
  LISTEN_MS,
  LOOKAHEAD_S,
  nearness,
  proximity,
  SAMPLE_COLUMNS,
  SAMPLE_ROWS,
  type Sounding,
  shownTerrain,
  skyLevel,
  soundingOf,
  sourceLevel,
} from "./ambience.ts";
import { startAnalytics } from "./analytics.ts";
import {
  creatureSound,
  FLOOD_NOTES,
  FLOOD_STEP_S,
  floodNotes,
  GameAudio,
  MAX_WRONG,
  placeSound,
  revealSound,
  WRONG_START_S,
} from "./audio.ts";
import {
  type Category,
  type ClearingState,
  catalog,
  categoryOf,
  clearingDress,
  clearingWorld,
  collectFinds,
  familyOf,
  homeChoices,
  loadFound,
  noteFinds,
  REACH_LARGE,
  REACH_SMALL,
  rememberFinds,
  setTurnTrial,
  showsChurch,
  type TurnTrial,
  tally,
  terrainOf,
} from "./clearing.ts";
import {
  addClearing,
  type Clearings,
  chooseClearing,
  chosenOf,
  deleteClearing,
  loadClearings,
  renameClearing,
  saveClearings,
  updateClearing,
} from "./clearings.ts";
import { type ControllerEvent, type DeathInfo, GameController } from "./controller.ts";
import { CONTACT_EMAIL, SHOW_GET_APP } from "./features.ts";
import { type Gallery, makeGallery } from "./gallery.ts";
import {
  askedLocale,
  dateLocale,
  detectLocale,
  formatDuration,
  type Locale,
  type StringKey,
  t,
  takesOne,
} from "./i18n.ts";
import { attachPointer } from "./input.ts";
import { Journey, journeyFrame, viewBetween } from "./journey.ts";
import { attachKeyboard } from "./keyboard.ts";
import { Lesson, type LessonHost } from "./lesson.ts";
import { type ListError, listEnabled, TodaysList } from "./list.ts";
import {
  codeInPath,
  loadTogether,
  type MemberView,
  memberTiles,
  PARTY_LIMIT,
  PartyConnection,
  partyInfo,
  partyLink,
  startParty,
} from "./party.ts";
import { makePortrait } from "./portrait.ts";
import {
  bestDaily,
  type DailyResult,
  dailyHistory,
  isAttempt,
  loadDailyResult,
  loadEndlessBest,
  loadEndlessStats,
  loadRun,
  streak,
} from "./runs.ts";
import {
  defaultScale,
  loadSettings,
  referenceScale,
  saveSettings,
  TILT_DEFAULT,
} from "./settings.ts";
import { shareResult } from "./share.ts";
import { getJson } from "./storage.ts";
import { Thumbnails } from "./thumbnails.ts";
import { ClearingEditor, type ClearingSound, entryName } from "./ui/clearing.ts";
import type { FindNote } from "./ui/finds.ts";
import { GAP as LESSON_CARD_GAP, LessonCard, type LessonWay } from "./ui/lesson.ts";
import type { ArchiveDay, ListPage, ListPageRow, MenuData } from "./ui/menu.ts";
import { PartyUi } from "./ui/party.ts";
import type { ResultListRow } from "./ui/result.ts";
import type { StartState, TodayState } from "./ui/start.ts";
import { applyTheme, type HudState, type Pick, type Place, type Reach, Ui } from "./ui.ts";
import {
  loadViews,
  NO_VIEWS,
  runName,
  saveViews,
  type Views,
  withClearingView,
  withRunView,
} from "./views.ts";

/** Device pixels per halftone cell; the 8 by 8 matrix makes dots 8 cells apart. */
const DOT_DEFAULT = 1;
const IDLE_FRAME_MS = 33;
/**
 * The closest zoom, times the reference scale, so it is the same on every screen: one tile about
 * fills a phone (Daniel, 2026-10-19, after trying it; five before).
 */
const MAX_ZOOM = 16;
/** The camera's turn with north up: the default view, 45 degrees around. */
const NORTH = Math.PI / 4;
/** How long the creature takes to rise before the result sheet comes up. */
const RESULT_DELAY_MS = 1400;
/**
 * The journey (decision 0031): it sets out once the sheet is up and the camera has glided to the
 * creature above it (450 ms), and a moment more.
 */
const JOURNEY_AFTER_SHEET_MS = 1250;
/** CSS pixels at the top of the screen the journey's view from above keeps clear: the status bar. */
const JOURNEY_TOP_PX = 32;
/** How long the cabin stays marked while the clearing says it can be changed (decision 0029). */
const CABIN_MARK_MS = 2400;
/** How long a field note of what a tap touched shows: it is read, not glanced at (decision 0023). */
const SAYING_MS = 3200;
/** What the clearing sounds whatever was touched; a thing set out and a ground have their own. */
const CLEARING_SOUNDS: Readonly<
  Record<Exclude<ClearingSound["kind"], "place" | "ground">, SoundId>
> = {
  turn: "effects.clearing-turn",
  clear: "effects.clearing-clear",
  refused: "effects.clearing-refused",
  cabin: "effects.tap-home",
  shutter: "effects.photo-shutter",
  share: "effects.share",
};

/** Query parameters used by tests and reference frames; harmless for players. */
interface Harness {
  readonly capture: boolean;
  readonly seed: number | null;
  readonly frozenTime: number | null;
  /** `&dot=N` sets the halftone cell, `&lock=screen` stops the dots following the world. */
  readonly dot: number;
  readonly lock: "world" | "screen";
  /** `?gallery` shows every model on its own tile instead of a run. */
  readonly gallery: boolean;
  /**
   * `?clearing` opens the game in the clearing (decision 0015): the clearing round the cabin where
   * the player sets out what they have found, a place beside Today and Endless. `&all=1` offers
   * every model, found or not, to try.
   */
  readonly clearing: boolean;
  readonly all: boolean;
  /** `&shadows=0` turns the objects' shadows off, for comparison. */
  readonly shadows: boolean;
  /**
   * To try another halftone: `&fine=4,2` blends the finer screen in from 4 points per art unit,
   * all of it from 2 on out, `&fine=all` uses it at every zoom, `&fine=1` only far out and
   * `&fine=0` not at all. Absent, FINE.
   */
  readonly fine: FineRange | null | undefined;
  /** `&life=0` keeps the animals out, for comparison. */
  readonly life: boolean;
  /**
   * To try other limits: `&maxzoom=4` lets the view zoom in to four times the reference scale
   * (sixteen otherwise, from 1 to 16), `&mintilt=18` stops the camera 18 degrees above the ground
   * (12 otherwise, from 5 to 30).
   */
  readonly maxZoom: number | null;
  readonly minTilt: number | null;
  /**
   * Decision 0010: the numbers shrink away where the tiles are too small on the screen to tap,
   * whole from tiles 32 points high and gone below about 27. `&hide=16,12` tries other heights,
   * `&hide=0` turns it off.
   */
  readonly hide: { readonly full: number; readonly gone: number } | null;
  /** Decision 0010: what stands in front hides numbers and banners; `&behind=0` turns it off. */
  readonly behind: boolean;
  /** `?debug` shows the developer settings: frame times and zoom. */
  readonly debug: boolean;
  /** A trial of turning in the clearing: `&turn=nudge` stands each thing a few degrees off its step. */
  readonly turn: TurnTrial;
  /** Tilt-shift: `&tilt=0.5` sets its strength, from 0 to 1, `&tilt=0` turns it off. */
  readonly tilt: number | null;
  /** `&grass=0` takes the grass off the ground, for comparison. */
  readonly grass: boolean;
  /**
   * `&journey=0` leaves out the journey of decision 0031, which after a death shows how the run
   * went in the world above the result sheet (`journey.ts`), for comparison.
   */
  readonly journey: boolean;
  /**
   * The lesson of decision 0045, the small world made by hand in which a new player learns the
   * game, opens by itself on a first visit. `?lesson` opens it for this visit whatever was played,
   * `?lesson=0` keeps it away, and `?lesson=new` applies the first visit's rule where `?capture`
   * would keep it away, for tests and pictures.
   */
  readonly lesson: "show" | "off" | "new" | null;
  /**
   * The banners a supporter chooses in the app (decision 0035): `&banner=b2` plants the flag
   * `iso-banner-b2-flag` on every bannered tile; absent or `1`, the game's own. The developer
   * settings switch it for the visit.
   */
  readonly banner: string | null;
  /** Daniel watches this party, from his page, live or played again with `&replay`. */
  readonly watch: string | null;
  readonly replay: boolean;
}

/** The flag model a banner's id names, `b2` for `iso-banner-b2-flag`; the game's own for null. */
function bannerFlag(variant: string | null): string {
  return `iso-banner-${variant ?? "1"}-flag`;
}

/** The banners the model set has flags for, the game's own first: `1`, `a1`, `a3` and so on. */
function bannerVariants(names: Iterable<string>): string[] {
  const variants = [...names].flatMap((n) => /^iso-banner-([a-z0-9]+)-flag$/.exec(n)?.[1] ?? []);
  return variants.sort((a, b) =>
    a === "1" ? -1 : b === "1" ? 1 : a.localeCompare(b, "en", { numeric: true }),
  );
}

/** A number from a query parameter, kept within lo and hi; null when absent or not a number. */
function limit(value: string | null, lo: number, hi: number): number | null {
  const n = value === null ? Number.NaN : Number(value);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : null;
}

/** Numbers are whole on tiles this many points high on the screen and gone from the second down. */
const HIDE = { full: 32, gone: 24 };

function hideFrom(value: string | null): { full: number; gone: number } | null {
  if (value === "0") return null;
  if (value === null) return HIDE;
  const [full, gone] = value.split(",").map(Number);
  if (value === "1" || full === undefined || gone === undefined) return HIDE;
  if (!(Number.isFinite(full) && Number.isFinite(gone) && full > gone && gone > 0)) {
    return HIDE;
  }
  return { full, gone };
}

/** The order a card picks its find from several: the rarest kind first. */
const FIND_ORDER: readonly Category[] = ["creatures", "houses", "stones", "plants"];

/** How far from the camera the run's ground comes out of the mist on the way back to it. */
const LIFT_REACH = 64;

/** The clearing's size as the menu of a held clearing names it. */
function sizeOf(state: ClearingState): Reach {
  return state.reach >= REACH_LARGE ? "large" : "small";
}

function readHarness(): Harness {
  const params = new URLSearchParams(location.search);
  const seed = params.get("seed");
  const time = params.get("time");
  return {
    capture: params.has("capture"),
    seed: seed === null ? null : Number(seed) >>> 0,
    frozenTime: time === null ? null : Number(time),
    dot: Math.max(1, Math.min(4, Number(params.get("dot") ?? DOT_DEFAULT) || DOT_DEFAULT)),
    lock: params.get("lock") === "screen" ? "screen" : "world",
    gallery: params.has("gallery"),
    clearing: params.has("clearing"),
    all: params.get("all") === "1",
    shadows: params.get("shadows") !== "0",
    fine: fineFrom(params.get("fine")),
    life: params.get("life") !== "0",
    maxZoom: limit(params.get("maxzoom"), 1, 16),
    minTilt: limit(params.get("mintilt"), 5, 30),
    hide: hideFrom(params.get("hide")),
    behind: params.get("behind") !== "0",
    debug: params.has("debug"),
    turn: params.get("turn") === "nudge" ? "nudge" : null,
    tilt: limit(params.get("tilt"), 0, 1),
    grass: params.get("grass") !== "0",
    journey: params.get("journey") !== "0",
    lesson: !params.has("lesson")
      ? null
      : params.get("lesson") === "0"
        ? "off"
        : params.get("lesson") === "new"
          ? "new"
          : "show",
    banner: /^[a-z0-9]+$/.exec(params.get("banner") ?? "")?.[0] ?? null,
    watch:
      /^[a-hjkmnp-z2-9]{6}(?:[a-hjkmnp-z2-9]{2})?$/.exec(params.get("watch") ?? "")?.[0] ?? null,
    replay: params.has("replay"),
  };
}

/** The portrait of each kind of creature (decision 0012) in each theme, by scripts/portraits.mjs. */
const PORTRAITS: Readonly<Record<"day" | "night", Readonly<Record<string, string>>>> = {
  day: {
    skogstroll: skogstrollUrl,
    huldra: huldraUrl,
    bergstroll: bergstrollUrl,
    jatte: jatteUrl,
    nacken: nackenUrl,
    sjotroll: sjotrollUrl,
  },
  night: {
    skogstroll: skogstrollNightUrl,
    huldra: huldraNightUrl,
    bergstroll: bergstrollNightUrl,
    jatte: jatteNightUrl,
    nacken: nackenNightUrl,
    sjotroll: sjotrollNightUrl,
  },
};

/** The heading and line for each kind; a giant on open ground has lines of its own. */
const CREATURE_COPY: Readonly<Record<string, readonly [StringKey, StringKey]>> = {
  skogstroll: ["over.skogstroll", "over.skogstrollLine"],
  huldra: ["over.huldra", "over.huldraLine"],
  bergstroll: ["over.bergstroll", "over.bergstrollLine"],
  jatte: ["over.jatte", "over.jatteLine"],
  jatteMeadow: ["over.jatteMeadow", "over.jatteMeadowLine"],
  nacken: ["over.nacken", "over.nackenLine"],
  sjotroll: ["over.sjotroll", "over.sjotrollLine"],
};

const MS_PER_DAY = 86_400_000;

/** "Friday 16 October" and "fredag 16 oktober" for a day number, which is a UTC day. */
function dateLong(day: number, locale: Locale): string {
  const text = new Intl.DateTimeFormat(dateLocale(locale), {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(day * MS_PER_DAY));
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * "Fri 16 Oct" and "fre 16 okt": Swedish without its abbreviations' full stops, every other
 * language with them, since German writes the day "16." and French its months "oct.". No
 * language keeps a comma after the weekday ("vie, 16 oct", "Fr., 16. Okt."), which the kickers'
 * own commas would crowd.
 */
function dateShort(day: number, locale: Locale): string {
  const text = new Intl.DateTimeFormat(dateLocale(locale), {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  })
    .format(new Date(day * MS_PER_DAY))
    .replaceAll(", ", " ");
  return locale === "sv" ? text.replaceAll(".", "") : text;
}

/** Where the finer halftone blends in, in points per art unit: none at `from`, all of it at `to`. */
interface FineRange {
  readonly from: number;
  readonly to: number;
}

/**
 * The finer halftone as the camera zooms out (Daniel, 2026-10-19, on desktop and then on the
 * iPhone): from 8 points per art unit, all of it from 4 on out, so the view is calm at the
 * starting zoom and the bold dots come back toward the closest zoom.
 */
const FINE: FineRange = { from: 8, to: 4 };
/** `&fine=1`: only far out, from 0.75 points per art unit down to 0.5. */
const FINE_FAR: FineRange = { from: 0.75, to: 0.5 };
/** `&fine=all`: finer at every zoom the camera reaches. */
const FINE_ALL: FineRange = { from: 1e6, to: 1e5 };

/** `&fine`: undefined when absent, null for none. */
function fineFrom(value: string | null): FineRange | null | undefined {
  if (value === null) return undefined;
  if (value === "0") return null;
  if (value === "all") return FINE_ALL;
  const [from, to] = value.split(",").map(Number);
  if (from !== undefined && to !== undefined && from > to && to > 0) return { from, to };
  return FINE_FAR;
}

/** The finer halftone's range in device pixels per art unit, where the renderer needs it. */
function fineDotsFor(range: FineRange | null): { from: number; to: number } | null {
  return range && { from: range.from * devicePixelRatio, to: range.to * devicePixelRatio };
}

/** cubic-bezier(0.2, 0.7, 0.2, 1), the glide of the compass back to north. */
function glideEase(p: number): number {
  const x1 = 0.2;
  const y1 = 0.7;
  const x2 = 0.2;
  const y2 = 1;
  const bez = (t: number, a: number, b: number) =>
    3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 20; i += 1) {
    const mid = (lo + hi) / 2;
    if (bez(mid, x1, x2) < p) lo = mid;
    else hi = mid;
  }
  return bez((lo + hi) / 2, y1, y2);
}

async function main(): Promise<void> {
  const root = document.getElementById("app");
  if (!root) return;
  const harness = readHarness();
  const settings = loadSettings();
  // `?lang=` names a language for this visit only, drafts too: for pictures and for trying one.
  const asked = askedLocale();
  let locale: Locale = asked ?? settings.locale ?? detectLocale();
  const motionQuery = matchMedia("(prefers-reduced-motion: reduce)");
  let reducedMotion = settings.reduceMotion ?? motionQuery.matches;
  const darkQuery = matchMedia("(prefers-color-scheme: dark)");
  // A theme the theme lab shows in this page without saving it (decision 0034): the world and the
  // interface take its colours, and what is chosen by Day or Night, such as the creatures'
  // portraits, follows its appearance.
  let previewTheme: ThemeDef | null = null;
  const themeId = (): "day" | "night" =>
    previewTheme
      ? previewTheme.appearance === "dark"
        ? "night"
        : "day"
      : (settings.theme ?? (darkQuery.matches ? "night" : "day"));
  const currentTheme = (): ThemeDef => previewTheme ?? THEMES[themeId()];
  // Sound (decision 0022), off where pictures are taken; the first touch or key starts it. A film
  // of apps/web/scripts may hear what the game would play in its place (`hear` under ?capture).
  const gameAudio = harness.capture ? null : new GameAudio(settings.music, settings.sounds);
  let audio: GameAudio | null = gameAudio;
  // For a function that keeps the one it starts with, its closures included.
  const playing = () => audio;
  if (gameAudio) {
    const audio = gameAudio;
    const unlock = () => audio.unlock();
    window.addEventListener("pointerdown", unlock, { capture: true, passive: true });
    window.addEventListener("keydown", unlock, { capture: true, passive: true });
    // Every control of the interface taps as it is used, unless what it did sounds by itself, as
    // the compass or a thing set out in the clearing does; a switch sounds on or off once its
    // setting has taken effect, so turning the sounds on is heard and turning them off is not.
    root.addEventListener(
      "click",
      (e) => {
        const control = (e.target as Element | null)?.closest?.("button, a[href]");
        if (!control) return;
        const checked = control.getAttribute("aria-checked");
        if (checked === null) {
          const acted = audio.acted;
          setTimeout(() => {
            if (audio.acted === acted) audio.play("effects.ui-tap");
          }, 0);
          return;
        }
        const on = checked !== "true";
        setTimeout(() => audio.play(on ? "effects.ui-toggle-on" : "effects.ui-toggle-off"), 0);
      },
      { capture: true },
    );
  }

  const canvas = document.createElement("canvas");
  canvas.id = "world";
  canvas.setAttribute("aria-label", t(locale, "a11y.map"));
  root.append(canvas);

  let view: WorldRenderer;
  let gallery: Gallery | null = null;
  // The clearing: the player's clearings and the state of the one that shows, the world and
  // dressing drawn from it, and its controls, all made the first time the player goes there.
  let clearings: Clearings | null = null;
  let clearing: ClearingState | null = null;
  let clearingSource: { readonly world: WorldSource; readonly dress: Dress } | null = null;
  let editor: ClearingEditor | null = null;
  let modelNames: string[] = [];
  let pick: ModelPick | null = null;
  // The banner for this visit (decision 0035): the address's, switched in the developer
  // settings, never saved.
  let bannerTrial = harness.banner ?? "1";
  // Kept for the clearing's pictures, which a second renderer draws from the same models.
  let modelSet: ModelSet | null = null;
  let atlasImage: HTMLImageElement | null = null;
  // The tile marked in the clearing's world, held or being edited, and where a tile was last
  // pressed.
  let editTile: Coord | null = null;
  let pressAt: { x: number; y: number } | null = null;
  /** The point of the ground pressed, in tiles, while a press is handled: where a massif goes. */
  let pressPoint: { x: number; y: number } | null = null;
  /** The device pixel of the press being acted on, for what it touched; none from the keyboard. */
  let pressPixel: { x: number; y: number } | null = null;
  // Where the player is: on the expedition, or in the clearing beside it; where the camera was
  // left on the run of each mode and in each clearing, by its id, so each comes back as it was
  // left, after a reload too (spec/formats.md, Views), though a run on a chosen seed, for pictures
  // and tests, keeps them for the visit alone; and the run the camera shows.
  let place: Place = "run";
  const keepsViews = harness.seed === null;
  let views: Views = keepsViews ? loadViews() : NO_VIEWS;
  let shownRun: { readonly mode: Mode; readonly name: string } | null = null;
  // Whether the saved runs have been read for finds since the page opened.
  let collected = false;
  // `?portrait=skogstroll` stages a creature for the picture on the card after death.
  const portrait = makePortrait(new URLSearchParams(location.search));
  const home = portrait?.centre ?? { x: 0, y: 0 };
  try {
    const image = new Image();
    image.src = atlasUrl;
    const [models] = await Promise.all([loadModels(worldGlbUrl), image.decode()]);
    modelSet = models;
    atlasImage = image;
    if (harness.gallery) gallery = makeGallery(models.models.keys());
    modelNames = [...models.models.keys()];
    pick = makePick(modelNames);
    view = new WorldRenderer(canvas, {
      theme: currentTheme(),
      scale: settings.scale ?? defaultScale(devicePixelRatio),
      dot: harness.dot,
      lock: harness.lock,
      shadows: harness.shadows,
      grass: harness.grass,
      maxScale: (harness.maxZoom ?? MAX_ZOOM) * referenceScale(devicePixelRatio),
      ...(harness.minTilt === null ? {} : { minPitch: (harness.minTilt * Math.PI) / 180 }),
      life: harness.life,
      banner: bannerFlag(bannerTrial),
      markersBehind: harness.behind,
      numberFade: harness.hide && {
        full: harness.hide.full * devicePixelRatio,
        gone: harness.hide.gone * devicePixelRatio,
      },
      // The finer halftone as the camera zooms out, or `&fine`.
      fineDots: fineDotsFor(harness.fine === undefined ? FINE : harness.fine),
      reducedMotion,
      preserveDrawingBuffer: harness.capture,
      atlas: { def: ATLAS, image },
      models,
      ...(gallery
        ? { dress: gallery.dress }
        : portrait
          ? {
              dress: portrait.dress,
              killerRim: false,
              ...(portrait.model ? { creature: portrait.model } : {}),
            }
          : {}),
    });
  } catch (error) {
    console.error(error);
    const message = document.createElement("p");
    message.className = "fatal";
    message.textContent = t(locale, "webgl.missing");
    root.append(message);
    return;
  }
  applyTheme(currentTheme());
  // Tilt-shift: the address's strength, else the developer setting's, else the default; none under
  // reduced motion, since it is a depth-of-field effect (Daniel, 2026-10-24).
  const applyTilt = () =>
    view.setTiltShift(reducedMotion ? 0 : (harness.tilt ?? settings.tiltShift ?? TILT_DEFAULT));
  applyTilt();
  // The clearing's controls stand between the world and the game's interface, under its bars.
  const clearingHost = document.createElement("div");
  root.append(clearingHost);

  let needsFrame = true;
  let panning = false;
  let cursor: Coord | null = null;
  let cursorVisible = false;
  let frameTimes: number[] = [];
  let lastFrame = 0;
  let lastDeath: DeathInfo | null = null;
  let resultTimer: ReturnType<typeof setTimeout> | null = null;
  /** Whether the menu is open, so its sheet sounds only as it opens and closes. */
  let menuOpen = false;

  const today = () => dayNumber(Date.now());
  // A distance as the interface shows it: the core's, held together by a no-break space so a line
  // never ends between "340" and "m".
  const fmt = (metres: number) => formatDistance(metres, locale).replace(" ", "\u00a0");
  const tr = (key: StringKey, params: Record<string, string | number> = {}) =>
    t(locale, key, params);
  const days = (n: number) =>
    tr(takesOne(locale, n) ? "over.streakDay" : "over.streakDays", { days: n });

  const ui = new Ui(root, locale, {
    pickMode: (mode) => switchMode(mode),
    banner: () => {
      settings.bannerMode = !settings.bannerMode;
      saveSettings(settings);
      ui.setBannerMode(settings.bannerMode);
      ui.showNote(tr(settings.bannerMode ? "note.bannerOn" : "note.bannerOff"));
    },
    cabin: () => {
      goHome();
      ui.showNote(tr("note.cabin"));
    },
    north: () => {
      audio?.act("effects.compass");
      turnNorth();
      ui.showNote(tr("note.north"));
    },
    clearing: () => {
      // A field note says what the clearing is for, as Today and Endless say theirs
      // (Daniel, 2026-10-19).
      if (enterClearing()) ui.showNote(clearingNote());
    },
    clearingMode: (mode) => editor?.setPhoto(mode === "photo"),
    reach: (size) => editor?.setSize(size === "large" ? REACH_LARGE : REACH_SMALL),
    pickClearing: (id) => {
      if (clearings && id !== clearings.chosen) switchClearing(chooseClearing(clearings, id));
    },
    addClearing: () => {
      if (!clearings) return;
      const next = addClearing(clearings);
      switchClearing(next);
      ui.showNote(tr("clearing.made", { name: chosenOf(next).name }));
    },
    renameClearing: (name) => {
      if (!clearings) return;
      const next = renameClearing(clearings, clearings.chosen, name);
      if (next === clearings) return;
      clearings = next;
      saveClearings(next);
      showClearings();
    },
    deleteClearing: () => {
      if (!clearings) return;
      const gone = chosenOf(clearings);
      const next = deleteClearing(clearings, gone.id);
      if (next === clearings) return;
      switchClearing(next);
      views = withClearingView(views, gone.id, null);
      if (keepsViews) saveViews(views);
      editor?.forget(gone.id);
      ui.showNote(tr("clearing.deleted", { name: gone.name }));
    },
    turn: (step) => turnBy((step * Math.PI) / 4),
    zoom: (step) => zoomStep(step, view.viewport.w / 2, view.viewport.h / 2),
    setOut: (pick) => setOut(pick),
    lesson: () => startLesson("menu"),
    share: () => void share(),
    again: () => {
      audio?.act("effects.set-out");
      settings.mode = "endless";
      saveSettings(settings);
      controller.mode = "endless";
      controller.restart(Date.now());
      play();
    },
    practice: () => {
      audio?.act("effects.set-out");
      settings.mode = "daily";
      saveSettings(settings);
      controller.mode = "daily";
      controller.restart(Date.now());
      play();
    },
    startNext: () => {
      audio?.act("effects.set-out");
      ui.closeDialogs();
      controller.start("daily", Date.now());
      play();
    },
    setTheme: (theme) => {
      settings.theme = theme;
      saveSettings(settings);
      themeChanged();
    },
    setThemeAuto: (on) => {
      settings.theme = on ? null : themeId();
      saveSettings(settings);
      themeChanged();
    },
    setLocale: (value) => {
      settings.locale = value;
      saveSettings(settings);
      locale = value ?? detectLocale();
      ui.setLocale(locale);
      document.documentElement.lang = locale;
      refreshAll();
      // The clearing's controls are made again in the new language when they next show.
      editor?.destroy();
      editor = null;
      editTile = null;
      if (place === "clearing") {
        clearingEditor()?.show();
        showTools();
      }
    },
    setReduceMotion: (on) => {
      settings.reduceMotion = on;
      saveSettings(settings);
      applyMotion();
    },
    setSounds: (on) => {
      settings.sounds = on;
      saveSettings(settings);
      audio?.setSounds(on);
    },
    setMusic: (on) => {
      settings.music = on;
      saveSettings(settings);
      audio?.setMusic(on);
    },
    setFrameTimes: (on) => {
      settings.debug = on;
      saveSettings(settings);
      if (!on) ui.setDebug(null);
    },
    setTilt: (amount) => {
      settings.tiltShift = amount;
      saveSettings(settings);
      applyTilt();
      needsFrame = true;
    },
    setBanner: (variant) => {
      bannerTrial = variant;
      view.setBanner(bannerFlag(variant));
      needsFrame = true;
    },
    external: (kind) => {
      const notes = { store: "placeholder.store" } as const;
      ui.showNote(tr(notes[kind]));
    },
    feedback: () => {
      const subject = tr("feedback.subject", { version: __APP_VERSION__ });
      location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
    },
    openUrl: (url) => {
      window.open(url, "_blank", "noopener");
    },
    menuData: () => menuData(),
    menuChanged: (open) => {
      needsFrame = true;
      if (open) journey?.interrupt();
      if (open !== menuOpen) audio?.play(open ? "effects.ui-sheet-open" : "effects.ui-sheet-close");
      menuOpen = open;
    },
    // A new find chimes as its card comes out, but not when the creature that ended the run is
    // the find: the mist has the moment.
    findShown: () => {
      if (controller.game.status !== "dead") audio?.play("effects.find-new");
    },
    listShown: () => {
      void todays.load(today());
    },
    listAdd: () => {
      const result = listTarget();
      if (!result) return;
      if (!todays.settings.name) {
        ui.openListName("add", "");
        return;
      }
      void todays.add(result).then((error) => {
        if (error && error !== "other") ui.showNote(listError(error));
      });
    },
    listName: (mode, name) => {
      const clean = normaliseName(name);
      if (!nameFits(clean)) {
        ui.listNameError(tr("list.errorName"));
        return;
      }
      const done = (error: ListError | null) => {
        if (error) ui.listNameError(listError(error));
        else ui.closeDialogs();
      };
      if (mode === "rename") {
        void todays.rename(clean).then(done);
        return;
      }
      const result = listTarget();
      if (result) void todays.add(result, clean).then(done);
      else ui.closeDialogs();
    },
    setListAuto: (on) => {
      todays.setAuto(on);
      if (on) void todays.catchUp(Date.now(), loadDailyResult, true);
    },
    listRemove: () => {
      void todays.forget().then((error) => {
        ui.showNote(error ? listError(error) : tr("note.listRemoved"));
      });
    },
    listReport: (id) => {
      void todays.report(id).then((error) => {
        ui.showNote(error ? listError(error) : tr("note.listReported"));
      });
    },
  });
  ui.setBannerMode(settings.bannerMode);

  // Today's expeditions (decision 0039): the place on the result sheet of a daily attempt, the
  // expedition added under a name, the page in the menu. Nothing is sent until the player adds an
  // expedition.
  const todays = new TodaysList({
    enabled: listEnabled(location.search) && !harness.gallery && portrait === null,
    changed: () => listChanged(),
  });
  /** The day's result whose place the result sheet shows, while it shows one. */
  let listResult: DailyResult | null = null;
  document.documentElement.lang = locale;

  // What sounds once a run: the horn as an endless run passes the best it set out against, and
  // the bell of the first stave church it comes upon. A run resumed after either stays quiet.
  let bestBefore: number | null = null;
  let passedBest = false;
  let belled = false;

  const controller = new GameController((event: ControllerEvent) => {
    if (event.type === "run") {
      // Out of a party: its line goes, and with it the members and their lanterns.
      if (!controller.party) dropParty();
      // The journey of the run before ends, and gives the camera back as it found it.
      journey?.leave();
      listResult = null;
      ui.setListPlace(null);
      // A new run shows on the expedition, out of the clearing.
      leaveClearing();
      ui.closeDialogs();
      if (resultTimer !== null) clearTimeout(resultTimer);
      resultTimer = null;
      view.startRun();
      audio?.duck(false);
      // The run that showed keeps its view, and the one that shows takes its own.
      keepView();
      showRunView();
      view.invalidate();
      refreshHud();
      needsFrame = true;
      // The ground a run opens on, or all a resumed run has shown, may hold finds too.
      noteNewFinds(controller.game.revealedTiles());
      bestBefore =
        controller.mode === "endless" ? (controller.endlessBest()?.metres ?? null) : null;
      passedBest = beyondBest();
      belled = churchShown(controller.game.revealedTiles());
    } else if (event.type === "changed") {
      // Under a frozen test clock the fade has already run, so the frame shows the result.
      const now = harness.frozenTime === null ? performance.now() : harness.frozenTime - FADE_MS;
      view.fog.markRevealed(event.revealed, now);
      view.invalidate();
      refreshHud();
      needsFrame = true;
      // What the action revealed and the tile it was taken on: a banner can close a number.
      noteNewFinds([...event.revealed, event.at]);
      soundOfChange(event.revealed, event.at);
      if (controller.together) refreshParty();
    } else if (event.type === "fell") {
      // A member of a party fell (decision 0043): the chasm opens with its creature, and the
      // others go on. Who fell watches, the camera following the nearest one still out.
      view.invalidate();
      needsFrame = true;
      audio?.play("effects.creature-wake");
      if (event.you) {
        vibrate([30, 40, 60]);
        ui.showNote(tr("party.youFell"), SAYING_MS);
        follow(nearestOut(event.at));
      } else {
        ui.showNote(tr("party.memberFell", { name: memberName(event.member) }), SAYING_MS);
        if (following === event.member) follow(nearestOut(event.at));
      }
      refreshParty();
    } else if (event.type === "died") {
      if (controller.together) {
        following = null;
        refreshParty();
      }
      vibrate([30, 40, 60]);
      audio?.duck(true);
      soundOfDeath();
      lastDeath = event.info;
      // Today's expeditions: the attempt added by itself when the player chose so, else its place
      // among the day's fetched for the result sheet.
      const day = controller.game.day;
      listResult =
        todays.enabled && event.info.daily?.attempt && day !== null ? loadDailyResult(day) : null;
      if (listResult) {
        if (todays.settings.auto && todays.settings.name) {
          void todays.add(listResult, todays.settings.name, true);
        } else void todays.refresh(listResult.day);
      }
      // The result sheet is set going first, so nothing the finds do below can keep it away.
      resultTimer = setTimeout(
        () => {
          resultTimer = null;
          showResult(event.info);
          if (!reducedMotion) journey?.begin(JOURNEY_AFTER_SHEET_MS);
        },
        reducedMotion ? 0 : RESULT_DELAY_MS,
      );
      // The creature that ended the run is a find too.
      noteNewFinds([]);
      // What the run found is kept for the clearing, after the frame of the death.
      const finder = pick;
      const game = controller.game;
      if (finder) {
        setTimeout(() => {
          try {
            rememberFinds(game, finder);
          } catch (error) {
            console.error(error);
          }
        }, 0);
      }
    } else if (event.type === "dayChanged") {
      const distance = fmt(event.sealed?.metres ?? controller.game.metres);
      const n = (event.nextExpedition - 1).toString();
      ui.showSealed(
        tr("sealed.title", { n }),
        tr("sealed.body", { distance }),
        tr("sealed.next", { n: event.nextExpedition }),
      );
      audio?.play("effects.sealed");
      refreshHud();
      // A sealed attempt joins the list by itself when the player chose so.
      if (todays.enabled) void todays.catchUp(Date.now(), loadDailyResult);
    }
  });

  /**
   * The sound of an action (decision 0022): a banner planted or taken away, or tiles revealed,
   * the tile's own ground and, for a flood, notes rising one more for each doubling; and once a
   * run, the horn of a new endless best and the bell of a stave church. A death has its own
   * sounds, with the event that follows.
   */
  function soundOfChange(revealed: readonly Coord[], at: Coord): void {
    const game = controller.game;
    if (!audio || game.status === "dead") return;
    soundOfMove(game, revealed, at);
    if (!passedBest && beyondBest()) {
      passedBest = true;
      audio.play("effects.record-new");
    }
    // A banner can close a number, and the tiles round it come to bear what stands there.
    if (!belled && churchShown([...revealed, at])) {
      belled = true;
      audio.play("effects.find-church-bell");
    }
  }

  /** A banner planted or taken away, or tiles revealed with their ground's sound and a flood's notes. */
  function soundOfMove(game: Game, revealed: readonly Coord[], at: Coord): void {
    if (!audio) return;
    if (revealed.length === 0) {
      audio.play(game.hasBanner(at.x, at.y) ? "effects.banner-plant" : "effects.banner-remove");
      return;
    }
    const tile = revealed.find((t) => t.x === at.x && t.y === at.y) ?? revealed[0];
    if (tile) audio.play(revealSound(game.terrain(tile.x, tile.y)));
    const notes = floodNotes(revealed.length);
    for (let i = 0; i < notes; i++) audio.play("effects.flood", i * FLOOD_STEP_S, FLOOD_NOTES[i]);
  }

  /** Whether an endless run has gone further than the best there was when it set out. */
  function beyondBest(): boolean {
    return bestBefore !== null && controller.game.metres > bestBefore;
  }

  /** Whether the tiles show a stave church, on an expedition: not in the gallery or a portrait. */
  function churchShown(tiles: readonly Coord[]): boolean {
    const finder = pick;
    if (!finder || gallery !== null || portrait !== null) return false;
    try {
      return showsChurch(controller.game, tiles, finder);
    } catch (error) {
      // A bell is a nicety: nothing here may stop the run.
      console.error(error);
      return false;
    }
  }

  /**
   * The mist rolls in, and the creature that ended the run sounds with it, at the tap; a beat
   * later each banner that stood wrong knocks, a sixteenth apart.
   */
  function soundOfDeath(): void {
    const game = controller.game;
    if (!audio) return;
    audio.play("effects.creature-wake");
    const wrong = game.bannerTiles().filter((t) => !game.isChasm(t.x, t.y)).length;
    for (let i = 0; i < Math.min(wrong, MAX_WRONG); i++) {
      audio.play("effects.banner-wrong", WRONG_START_S + i * FLOOD_STEP_S);
    }
    const killer = game.killer;
    if (!killer) return;
    const kind = creatureKind(creatureOn(game.terrain(killer.x, killer.y), killer.x, killer.y));
    const sound = creatureSound(kind);
    if (sound) audio.play(sound);
  }

  /** What the saved runs hold: the daily expeditions of the last 60 days and the current runs. */
  function savedRecords(): RunRecord[] {
    const records: RunRecord[] = [];
    for (const { result } of dailyHistory(Date.now(), 60)) if (result) records.push(result.record);
    for (const mode of ["daily", "endless"] as const) {
      const saved = loadRun(mode);
      if (saved) records.push(saved);
    }
    return records;
  }

  // What has been found, in the HUD, and a card for each new find the moment its tile is
  // revealed, with its picture from a second, small renderer. One such renderer for each theme,
  // shared by the cards and the clearing's tray.
  const pictures = new Map<string, Thumbnails>();

  function thumbnails(): Thumbnails | null {
    if (!modelSet || !atlasImage || !pick) return null;
    const id = themeId();
    let set = pictures.get(id);
    if (!set) {
      set = new Thumbnails(modelSet, { def: ATLAS, image: atlasImage }, currentTheme(), pick);
      pictures.set(id, set);
    }
    return set;
  }

  function showFound(): void {
    const { found, total } = tally(modelNames, loadFound());
    ui.setFound(found, total);
  }

  function findPicture(name: string): Promise<string | null> {
    return thumbnails()?.request(name) ?? Promise.resolve(null);
  }

  function noteNewFinds(tiles: readonly Coord[]): void {
    const finder = pick;
    // Only the expedition finds things: not the gallery or a portrait.
    if (!finder || gallery !== null || portrait !== null) return;
    // A find's card is a nicety: whatever goes wrong here must not stop the run or its result.
    try {
      const fresh = noteFinds(controller.game, tiles, finder);
      if (fresh.length === 0) return;
      const entries = catalog(modelNames, loadFound(), false);
      const byName = new Map(entries.map((e, i) => [e.name, { entry: e, i }]));
      // The card leads with the rarest kind of thing: a creature, a house, a stone, then a plant.
      const rank = (name: string) => {
        const known = byName.get(name);
        return known ? FIND_ORDER.indexOf(known.entry.category) * 1000 + known.i : 1e6;
      };
      fresh.sort((a, b) => rank(a) - rank(b));
      // Only the first of several shows its picture on the card.
      const notes: FindNote[] = fresh.map((name, i) => {
        const entry = byName.get(name)?.entry;
        return {
          label: entry ? entryName(locale, entry) : name,
          picture: i === 0 ? findPicture(name) : Promise.resolve(null),
        };
      });
      ui.noteFinds(notes, entries.filter((e) => e.found).length, entries.length);
    } catch (error) {
      console.error(error);
    }
  }

  function vibrate(pattern: number | number[]): void {
    try {
      navigator.vibrate?.(pattern);
    } catch {
      // Not supported; fine.
    }
  }

  function applyMotion(): void {
    reducedMotion = settings.reduceMotion ?? motionQuery.matches;
    view.setReducedMotion(reducedMotion);
    ui.setReducedMotion(reducedMotion);
    applyTilt();
    needsFrame = true;
  }
  ui.setReducedMotion(reducedMotion);
  motionQuery.addEventListener("change", () => {
    if (settings.reduceMotion === null) applyMotion();
  });

  function refreshHud(): void {
    // The lesson's distance, from its own ground, under the word for the furthest point.
    if (lesson) {
      const n = expeditionNumber(today());
      ui.setHud({
        mode: controller.mode,
        distance: fmt(lesson.game.metres),
        modeLine: tr("over.furthestLabel"),
        dailyShort: `#${n}`,
        endlessKicker: "",
      });
      return;
    }
    const game = controller.game;
    const n = expeditionNumber(today());
    const best = controller.endlessBest();
    let modeLine: string;
    if (controller.mode === "daily") {
      const run = controller.expedition() ?? n;
      modeLine = controller.attempt ? tr("mode.daily", { n: run }) : tr("hud.practice", { n: run });
    } else if (controller.together) {
      modeLine = tr("over.kickerTogether");
    } else {
      modeLine = best ? tr("mode.endlessBest", { distance: fmt(best.metres) }) : tr("mode.endless");
    }
    const state: HudState = {
      mode: controller.mode,
      distance: fmt(game.metres),
      modeLine,
      dailyShort: `#${n}`,
      // Every place has its line, Endless its record from 0 m on (Daniel, 2026-10-19).
      endlessKicker: tr("hud.best", { distance: fmt(best?.metres ?? 0) }),
    };
    ui.setHud(state);
    refreshParty();
  }

  function refreshAll(): void {
    refreshHud();
    if (ui.currentScreen === "start") ui.showStart(startState());
    if (ui.currentScreen === "end" && lastDeath) showResult(lastDeath);
  }

  /** Today's expedition: not started, in progress, or done, and how far it reached. */
  function todayInfo(): { state: TodayState; metres: number } {
    const day = today();
    const result = loadDailyResult(day);
    if (result) return { state: "done", metres: result.metres };
    const saved = loadRun("daily");
    if (saved && saved.day === day && isAttempt(saved) && saved.actions.length > 0) {
      const game = Game.fromRecord(saved);
      if (game.status === "playing") return { state: "progress", metres: game.metres };
    }
    return { state: "new", metres: 0 };
  }

  function startState(): StartState {
    const day = today();
    const info = todayInfo();
    const best = loadEndlessBest();
    return {
      today: info.state,
      expedition: expeditionNumber(day),
      todayDistance: fmt(info.metres),
      dateLong: dateLong(day, locale),
      dateShort: dateShort(day, locale),
      nextIn: formatDuration(locale, msUntilNextDay(Date.now())),
      endlessBest: best ? fmt(best.metres) : null,
    };
  }

  /** Into play: the HUD, and the start hint while the run has no reveal of its own. */
  function play(): void {
    leaveClearing();
    ui.setScreen("play");
    // The start hint shows on a phone only until the player's first reveal ever.
    if (controller.game.record.actions.length === 0 && !settings.hintSeen) ui.showStartHint();
    else ui.hideHint();
    refreshHud();
    needsFrame = true;
  }

  /** Starts or resumes a mode; a run that has ended gives way to a new one. */
  function begin(mode: Pick): void {
    settings.mode = mode;
    saveSettings(settings);
    controller.start(mode, Date.now());
    if (controller.game.status !== "playing") controller.restart(Date.now());
  }

  function setOut(pick: Pick): void {
    audio?.act("effects.set-out");
    begin(pick);
    play();
  }

  function switchMode(mode: Pick): void {
    const changed = mode !== controller.mode || ui.currentScreen !== "play";
    if (!changed && place === "run") return;
    // From the clearing back to the same mode, the run that waited goes on where it was left.
    if (changed || controller.game.status !== "playing") begin(mode);
    play();
    if (mode === "endless") ui.showNote(tr("note.endless"));
    else ui.showNote(tr(loadDailyResult(today()) ? "note.todayPractice" : "note.today"));
  }

  /** The result sheet for a death, the camera gliding so the creature stands above it. */
  function showResult(info: DeathInfo): void {
    const game = controller.game;
    const killer = game.killer;
    const terrain = killer ? game.terrain(killer.x, killer.y) : Terrain.forest;
    const model = killer ? creatureOn(terrain, killer.x, killer.y) : "iso-skogstroll-1";
    const kind = creatureKind(model);
    const copy =
      CREATURE_COPY[kind === "jatte" && terrain === Terrain.grass ? "jatteMeadow" : kind] ??
      CREATURE_COPY.skogstroll;
    const [headingKey, lineKey] = copy ?? ["over.title", "over.title"];
    const heading = tr(headingKey);
    const day = today();
    const n = expeditionNumber(game.day ?? day);
    const bestDay = bestDaily(Date.now());
    const todayDone = loadDailyResult(day) !== null;
    let kicker: string;
    let stats: [{ label: string; value: string }, { label: string; value: string }];
    let members: { name: string; line: string }[] | undefined;
    if (info.daily) {
      const best = { label: tr("over.yourBest"), value: fmt(bestDay?.metres ?? info.metres) };
      if (info.daily.attempt) {
        kicker = tr("over.kickerDaily", { n, dateShort: dateShort(game.day ?? day, locale) });
        stats = [{ label: tr("over.streak"), value: days(streak(Date.now())) }, best];
      } else {
        kicker = tr("over.kickerPractice", { n });
        stats = [{ label: tr("over.todayResult"), value: fmt(info.daily.attemptMetres) }, best];
      }
    } else if (info.together) {
      kicker = tr("over.kickerTogether");
      const found = memberTally();
      const you = controller.party?.you ?? 0;
      // Who found the most first, a friendly race within the party.
      members = Array.from({ length: controller.game.party }, (_, m) => ({
        m,
        t: found.get(m) ?? { tiles: 0, metres: 0 },
      }))
        .sort((a, b) => b.t.tiles - a.t.tiles || a.m - b.m)
        .map(({ m, t }) => ({
          name: m === you ? tr("party.you") : memberName(m),
          line: tr("over.memberLine", {
            tiles: partyUi.tilesText(t.tiles),
            distance: fmt(t.metres),
          }),
        }));
      stats = [
        { label: tr("over.togetherBest"), value: fmt(info.together.best) },
        { label: tr("over.togetherMembers"), value: String(controller.game.party) },
      ];
    } else {
      kicker = tr("over.kickerEndless");
      stats = [
        { label: tr("over.yourBest"), value: fmt(info.endless?.best ?? info.metres) },
        {
          label: tr("over.endlessRuns"),
          value: String(info.endless?.runs ?? loadEndlessStats().runs),
        },
      ];
    }
    const src = PORTRAITS[themeId()][kind];
    ui.showResult({
      kicker,
      heading,
      line: tr(lineKey),
      portrait: src ? { src, alt: heading } : null,
      furthest: fmt(info.metres),
      stats,
      shareText: controller.shareTextFor(locale),
      kind: info.daily ? "daily" : "endless",
      ...(members ? { members } : {}),
      note:
        info.daily || todayDone
          ? tr("daily.next", { time: formatDuration(locale, msUntilNextDay(Date.now())) })
          : tr("over.todayWaiting"),
    });
    ui.setListPlace(listResult && info.daily?.attempt ? listRow(listResult) : null);
    audio?.play("effects.ui-sheet-open");
    if (killer) frameAboveSheet(killer);
  }

  /** What the list shows has changed: the result sheet's row and the open page follow. */
  function listChanged(): void {
    if (listResult) ui.setListPlace(listRow(listResult));
    ui.renderPage();
  }

  /**
   * The head of a day's result sheet (decision 0040): the place as its heading, or Waiting to be
   * added, or the list's name while the place is not known, the list's line the link then; Add your
   * name while the expedition is not on the list, else the name it is on as.
   */
  function listRow(result: DailyResult): ResultListRow {
    const p = todays.place(result);
    let heading = tr("menu.list");
    let link = tr("menu.listLine");
    if (p.waiting) {
      heading = tr("list.waiting");
      link = tr("menu.list");
    } else if (p.place !== null && p.count !== null) {
      heading = tr("list.placeSoFar", { place: p.place, count: p.count });
      link = tr("menu.list");
    }
    return {
      heading,
      link,
      add: !p.added && !p.waiting,
      as: p.added && p.name ? tr("list.as", { name: p.name }) : "",
    };
  }

  function listError(error: ListError): string {
    const keys: Record<ListError, StringKey> = {
      name: "list.errorName",
      refused: "list.errorRefused",
      already: "list.errorAlready",
      removed: "list.errorOther",
      closed: "list.errorClosed",
      busy: "list.errorBusy",
      blocked: "list.errorBlocked",
      notFound: "list.errorOther",
      other: "list.errorOther",
    };
    return tr(keys[error]);
  }

  /** The day's result an Add adds: the one on the result sheet, else today's. */
  function listTarget(): DailyResult | null {
    return listResult ?? loadDailyResult(today());
  }

  /**
   * Today's expeditions for the menu's page: the first rows as last fetched, and the player's own
   * at the foot when it has ended and is not among them.
   */
  function listPage(): ListPage {
    const day = today();
    const fetched = todays.list(day);
    const kicker = tr("over.kickerDaily", {
      n: expeditionNumber(day),
      dateShort: dateShort(day, locale),
    });
    if (fetched === null || fetched === "unreachable") {
      return {
        state: fetched === null ? "loading" : "unreachable",
        kicker,
        count: null,
        rows: [],
        own: null,
      };
    }
    const ownId = todays.addedOn(day);
    const rows: ListPageRow[] = fetched.entries.map((e) => ({
      id: e.id,
      place: String(e.place),
      name: e.name,
      distance: fmt(e.metres),
      tiles: tr("list.tiles", { tiles: e.tiles }),
      map: e.map,
      own: e.id === ownId,
    }));
    const result = loadDailyResult(day);
    let own: ListPage["own"] = null;
    if (result && !rows.some((r) => r.own)) {
      const at = todays.place(result);
      own = {
        id: ownId,
        place: at.place === null ? "" : String(at.place),
        name: todays.settings.name,
        distance: fmt(result.metres),
        tiles: tr("list.tiles", { tiles: result.tiles }),
        map: result.shareMap,
        own: true,
        added: ownId !== null,
      };
    }
    const n = fetched.count;
    return {
      state: "ready",
      kicker,
      count: tr(takesOne(locale, n) ? "list.countSoFarOne" : "list.countSoFar", { count: n }),
      rows,
      own,
    };
  }

  /** Glides the camera so the creature stands in the world left visible above the sheet. */
  function frameAboveSheet(tile: Coord): void {
    const sheet = ui.resultHeight * devicePixelRatio;
    const from = view.focus();
    view.centreOnTile(tile.x, tile.y);
    view.panBy(0, -sheet / 2);
    const target = view.focus();
    view.setFocus(from);
    glideFocus(target, 450);
  }

  async function share(): Promise<void> {
    audio?.act("effects.share");
    const text = controller.shareTextFor(locale);
    const outcome = await shareResult(text);
    if (outcome === "shared") ui.setShared("shared");
    else if (outcome === "copied") ui.setShared("copied");
  }

  function menuData(): MenuData {
    const now = Date.now();
    const day = today();
    const best = bestDaily(now);
    const stats = loadEndlessStats();
    const endlessBest = loadEndlessBest();
    const progress = todayInfo();
    const history: ArchiveDay[] = [];
    let played = 0;
    for (const { day: d, result } of dailyHistory(now, 60)) {
      if (result) played += 1;
      let state: ArchiveDay["state"];
      let distance: string | null = null;
      let map: readonly string[] | null = null;
      if (result) {
        distance = fmt(result.metres);
        map = result.shareMap;
        state =
          d === day
            ? "today"
            : result.outcome === "sealed"
              ? "sealed"
              : best && best.day === d
                ? "best"
                : "played";
      } else if (d === day) {
        if (progress.state !== "progress") continue;
        const saved = loadRun("daily");
        state = "progress";
        distance = fmt(progress.metres);
        map = saved ? shareMap(Game.fromRecord(saved)) : null;
      } else {
        state = "notPlayed";
      }
      history.push({
        n: expeditionNumber(d),
        dateShort: dateShort(d, locale),
        state,
        distance,
        map,
      });
    }
    const streakDays = streak(now);
    return {
      web: true,
      getApp: SHOW_GET_APP,
      desk: ui.isDesk,
      debug: harness.debug,
      streak: days(streakDays),
      streakDays,
      bestDaily: best ? fmt(best.metres) : null,
      expeditionsPlayed: played,
      history,
      endless: {
        best: endlessBest ? fmt(endlessBest.metres) : null,
        last: stats.runs > 0 ? fmt(stats.last) : null,
        runs: stats.runs,
      },
      theme: themeId(),
      themeAuto: settings.theme === null,
      locale,
      localeChosen: settings.locale,
      systemLocale: detectLocale(),
      reduceMotion: reducedMotion,
      music: settings.music,
      sounds: settings.sounds,
      frameTimes: settings.debug,
      tilt: settings.tiltShift ?? TILT_DEFAULT,
      banner: bannerTrial,
      banners: bannerVariants(modelNames),
      version: __APP_VERSION__,
      email: CONTACT_EMAIL,
      list: todays.enabled ? listPage() : null,
      listSettings: todays.enabled
        ? { name: todays.settings.name, auto: todays.settings.auto }
        : null,
    };
  }

  /**
   * The camera's eased motions under way: a glide to a place, a turn, the zoom toward readable
   * numbers. A finger or a click coming down on the world stops them, as it stops the glide after
   * a pan (decision 0021).
   */
  const easings = new Set<{ stopped: boolean }>();

  /**
   * Calls `frame` on every frame with the share of `ms` gone, from 0 to 1, then `done`, unless a
   * finger or a click stops it first.
   */
  function ease(ms: number, frame: (p: number) => void, done?: () => void): void {
    const motion = { stopped: false };
    easings.add(motion);
    const start = performance.now();
    const step = (now: number) => {
      if (motion.stopped) return;
      const p = Math.min(1, Math.max(0, (now - start) / ms));
      frame(p);
      needsFrame = true;
      if (p < 1) {
        requestAnimationFrame(step);
        return;
      }
      easings.delete(motion);
      done?.();
    };
    requestAnimationFrame(step);
  }

  /** A finger or a click came down on the world: the camera stops. Whether it was moving. */
  function stopCamera(): boolean {
    const moving = view.isGliding || easings.size > 0;
    view.stopGlide();
    for (const motion of easings) motion.stopped = true;
    easings.clear();
    return moving;
  }

  /** Moves the camera's focus to a point over a time, eased, or at once under reduced motion. */
  function glideFocus(target: { x: number; y: number }, ms: number): void {
    const from = view.focus();
    if (reducedMotion) {
      view.setFocus(target);
      view.settle();
      needsFrame = true;
      return;
    }
    ease(
      ms,
      (p) => {
        const e = 1 - (1 - p) ** 3;
        view.setFocus({ x: from.x + (target.x - from.x) * e, y: from.y + (target.y - from.y) * e });
      },
      () => view.settle(),
    );
  }

  /** The journey of decision 0031: how the run went, shown above the sheet after its death. */
  const journey = harness.journey
    ? new Journey({
        view,
        game: () => controller.game,
        area: () => ({
          w: view.viewport.w,
          h: view.viewport.h,
          top: JOURNEY_TOP_PX * devicePixelRatio,
          bottom: ui.resultHeight * devicePixelRatio,
        }),
        scales: () => ({ min: MIN_SCALE, max: defaultScale(devicePixelRatio) }),
        // Not while a finger moves the world: it would set out under the finger.
        ready: () =>
          place === "run" && controller.game.status === "dead" && !reducedMotion && !panning,
        sheetUp: () => ui.currentScreen === "end",
        ease,
        stop: () => {
          stopCamera();
        },
        frame: () => {
          needsFrame = true;
        },
      })
    : null;
  // A key while it plays shows its end at once, as a touch does.
  if (journey) addEventListener("keydown", () => journey.interrupt(), { capture: true });

  /**
   * The lesson (decision 0045): a small world made by hand, played to learn the game and kept
   * nowhere. While it shows, it is the world the renderer draws and what a tap acts on, its card
   * stands over the world and the HUD keeps the banner button alone.
   */
  let lesson: Lesson | null = null;
  let lessonCard: LessonCard | null = null;
  /**
   * The lesson's camera: higher than a run's, so the trees in front hide no number, and close
   * enough that each step's tiles fill the world above the card, from as close as a run starts to
   * this much closer.
   */
  const LESSON_PITCH = (60 * Math.PI) / 180;
  const LESSON_CLOSER = 1.6;
  const LESSON_GLIDE_MS = 700;

  const lessonHost: LessonHost = {
    changed: (game, revealed, at) => {
      // Under a frozen test clock the fade has already run, so the frame shows the result.
      const now = harness.frozenTime === null ? performance.now() : harness.frozenTime - FADE_MS;
      view.fog.markRevealed(revealed, now);
      view.invalidate();
      needsFrame = true;
      soundOfMove(game, revealed, at);
      refreshHud();
    },
    woke: () => {
      vibrate([30, 40, 60]);
      audio?.play("effects.creature-wake");
      ui.showNote(tr("lesson.woke"), SAYING_MS);
      view.invalidate();
      needsFrame = true;
    },
    sink: (at) => {
      const now = harness.frozenTime === null ? performance.now() : harness.frozenTime - FADE_MS;
      view.fog.close([at], now);
      view.lowerCreatures(now);
      needsFrame = true;
    },
    settled: () => {
      view.fog.open();
      view.forgetCreatures();
      view.invalidate();
      needsFrame = true;
    },
    noCreature: () => ui.showNote(tr("lesson.noCreature")),
    step: (index, step) => {
      lessonCard?.show(index, step);
      placeLessonCamera(index, step, index > 0);
    },
    frame: () => {
      view.invalidate();
      needsFrame = true;
    },
  };

  /** How the lesson that shows was opened, what showed before it, and what comes after it. */
  let lessonWay: LessonWay = "first";
  let lessonFrom: "start" | "play" | "end" = "start";
  let lessonThen: (() => void) | null = null;

  /**
   * Whether the lesson opens by itself (decision 0045): for a visitor who has never played, the
   * first time, unless the address says otherwise. Pictures and tests keep it away.
   */
  function lessonFirst(): boolean {
    if (harness.lesson === "show") return true;
    if (harness.lesson === "off" || (harness.lesson === null && harness.capture)) return false;
    return !settings.lessonSeen && !settings.hintSeen && !played();
  }

  /** Whether anything was ever played in this browser, from before the start hint was kept too. */
  function played(): boolean {
    const now = Date.now();
    return (
      (["daily", "endless"] as const).some((m) => (loadRun(m)?.actions.length ?? 0) > 0) ||
      bestDaily(now) !== null ||
      loadEndlessBest() !== null ||
      loadEndlessStats().runs > 0 ||
      loadTogether() !== null
    );
  }

  /**
   * Opens the lesson over whatever showed: its world, its card and its camera. `then` follows it,
   * played or skipped: the sheet to join friends after an invitation.
   */
  function startLesson(way: LessonWay, then: (() => void) | null = null): void {
    if (lesson) return;
    lessonWay = way;
    lessonThen = then;
    const screen = ui.currentScreen;
    lessonFrom = screen === "end" && lastDeath ? "end" : screen === "start" ? "start" : "play";
    journey?.leave();
    leaveClearing();
    ui.closeDialogs();
    ui.setScreen("play");
    ui.hideHint();
    if (settings.bannerMode) {
      settings.bannerMode = false;
      saveSettings(settings);
      ui.setBannerMode(false);
    }
    keepView();
    lesson = new Lesson(lessonHost);
    lessonCard = new LessonCard(
      ui.element,
      tr,
      way,
      () => ui.isDesk,
      () => fmt(lesson?.game.metres ?? 0),
      {
        skip: () => leaveLesson(false),
        finish: () => leaveLesson(true),
      },
    );
    view.fog.open();
    view.startRun();
    view.forgetCreatures();
    view.setCameraView({
      x: 0,
      y: 0,
      yaw: NORTH,
      pitch: LESSON_PITCH,
      scale: defaultScale(devicePixelRatio),
    });
    view.invalidate();
    refreshHud();
    lesson.start();
    needsFrame = true;
  }

  /**
   * Leaves the lesson, played to its end (`finished`) or skipped; either way it does not open by
   * itself again. A first visit sets out into today's expedition, or Endless once it is done, or
   * goes to the start; after an invitation the sheet to join opens over the start; from How to
   * play the player is back where they were.
   */
  function leaveLesson(finished: boolean): void {
    if (!lesson) return;
    lesson.dispose();
    lessonCard?.remove();
    lesson = null;
    lessonCard = null;
    settings.lessonSeen = true;
    saveSettings(settings);
    stopCamera();
    view.fog.open();
    view.startRun();
    view.forgetCreatures();
    showRunView();
    view.invalidate();
    needsFrame = true;
    const then = lessonThen;
    lessonThen = null;
    if (lessonWay === "first" && finished) {
      setOut(loadDailyResult(today()) ? "endless" : "daily");
    } else if (lessonWay === "menu" && lessonFrom === "play") {
      play();
    } else if (lessonWay === "menu" && lessonFrom === "end" && lastDeath) {
      play();
      showResult(lastDeath);
    } else {
      refreshHud();
      ui.showStart(startState());
    }
    then?.();
  }

  /**
   * The tiles a step shows: the nine of the number it explains, or on the first the cabin's nine,
   * and the tile its ring marks; at the end the creatures under the banners. The camera frames
   * them and, on a desktop, the card stands under them.
   */
  function lessonTiles(index: number, step: LessonStep): Coord[] {
    const game = lesson?.game;
    if (!game) return [];
    const nine = (c: Coord) =>
      [-1, 0, 1].flatMap((dy) => [-1, 0, 1].map((dx) => ({ x: c.x + dx, y: c.y + dy })));
    const tiles: Coord[] = [];
    if (step.frame) tiles.push(...nine(step.frame));
    else if (index === 0) tiles.push(...nine({ x: 0, y: 0 }));
    if (step.ring) tiles.push(step.ring);
    // At the end the camera stands back a little, over all the lesson showed.
    if (tiles.length === 0) {
      tiles.push(...nine({ x: 0, y: 0 }));
      for (const s of LESSON_STEPS) {
        if (s.frame) tiles.push(...nine(s.frame));
        if (s.ring) tiles.push(s.ring);
      }
    }
    return tiles;
  }

  /**
   * Turns the camera to a step: its tiles fill the world that shows above the card, with a tile
   * of margin round them, in the middle of it.
   */
  function placeLessonCamera(index: number, step: LessonStep, glide: boolean): void {
    const tiles = lessonTiles(index, step);
    if (tiles.length === 0 || !lessonCard) return;
    const dpr = devicePixelRatio;
    const card = (lessonCard.height + LESSON_CARD_GAP) * dpr;
    const start = defaultScale(dpr);
    const to = journeyFrame(
      tiles,
      NORTH,
      LESSON_PITCH,
      // On a phone the card lies over the foot of the screen; on a desktop it stands under the
      // tiles, so the room it needs is left below them.
      {
        w: view.viewport.w,
        h: view.viewport.h,
        top: LESSON_CARD_GAP * dpr,
        bottom: ui.isDesk ? card : view.viewport.h - lessonCardTop() * dpr,
      },
      start,
      start * LESSON_CLOSER,
    );
    const target = { ...to, pitch: LESSON_PITCH };
    if (!glide || reducedMotion) {
      view.setCameraView({ ...target, yaw: NORTH });
      view.settle();
      needsFrame = true;
      return;
    }
    const from = view.cameraView();
    ease(
      LESSON_GLIDE_MS,
      (p) => view.setCameraView(viewBetween(from, target, 1 - (1 - p) ** 3)),
      () => view.settle(),
    );
  }

  /** Where the card's top stands on a phone, in CSS pixels. */
  function lessonCardTop(): number {
    const card = ui.element.querySelector(".lesson-card")?.getBoundingClientRect();
    return card ? card.top : innerHeight;
  }

  function goHome(): void {
    const from = view.focus();
    view.centreOnTile(0, 0);
    const target = view.focus();
    view.setFocus(from);
    glideFocus(target, 350);
  }

  /** Turns the camera back to north up, the short way round, over 360 ms. */
  function turnNorth(): void {
    let delta = NORTH - view.yaw;
    delta = Math.atan2(Math.sin(delta), Math.cos(delta));
    const cx = view.viewport.w / 2;
    const cy = view.viewport.h / 2;
    if (reducedMotion) {
      view.rotateBy(delta, cx, cy);
      needsFrame = true;
      return;
    }
    let done = 0;
    ease(360, (p) => {
      const e = glideEase(p);
      view.rotateBy(delta * e - done, cx, cy);
      done = delta * e;
    });
  }

  /**
   * Decision 0010: where the numbers are too small to read, a tap or hold must not reveal
   * a tile blind. It zooms in toward that point until the numbers are whole instead.
   */
  function tooSmallToTap(): boolean {
    return harness.hide !== null && !gallery && place === "run" && view.numberScale() < 0.5;
  }

  function zoomToRead(devX: number, devY: number): void {
    const from = view.scale;
    const to = view.readableScale();
    const done = () => {
      view.settle();
      rememberScale();
      needsFrame = true;
    };
    if (reducedMotion) {
      view.zoomAround(to, devX, devY);
      done();
      return;
    }
    ease(300, (p) => view.zoomAround(from + (to - from) * (1 - (1 - p) ** 3), devX, devY), done);
  }

  /** A tap, hold or secondary click at a device pixel: reveal or banner, or zoom in to read. */
  function press(devX: number, devY: number, banner: boolean): void {
    if (ui.currentScreen !== "play" && !gallery) return;
    // In the clearing's photo mode a finger only moves the camera.
    if (editor?.photographing) return;
    pressAt = { x: devX / devicePixelRatio, y: devY / devicePixelRatio };
    pressPoint = view.pointAt(devX, devY);
    pressPixel = { x: devX, y: devY };
    if (tooSmallToTap()) zoomToRead(devX, devY);
    else act(view.tileAt(devX, devY), banner);
    pressAt = null;
    pressPoint = null;
    pressPixel = null;
  }

  function zoomStep(step: number, devX: number, devY: number): void {
    view.zoomAround(view.scale * 1.25 ** step, devX, devY);
    view.settle();
    rememberScale();
    needsFrame = true;
  }

  /** Saves the zoom once the wheel has rested, not on every one of a trackpad's many events. */
  let rememberTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleRememberScale(): void {
    if (rememberTimer !== null) clearTimeout(rememberTimer);
    rememberTimer = setTimeout(() => {
      rememberTimer = null;
      rememberScale();
    }, 400);
  }

  /** Turns the world by an angle around the screen's centre, eased over a quarter second. */
  function turnBy(radians: number): void {
    const rotate = view.rotateBy.bind(view);
    const cx = view.viewport.w / 2;
    const cy = view.viewport.h / 2;
    if (reducedMotion) {
      rotate(radians, cx, cy);
      needsFrame = true;
      return;
    }
    let done = 0;
    ease(250, (p) => {
      const e = 1 - (1 - p) ** 3;
      rotate(radians * e - done, cx, cy);
      done = radians * e;
    });
  }

  function rememberScale(): void {
    settings.scale = view.scale;
    saveSettings(settings);
  }

  function act(tile: Coord, banner: boolean): void {
    if (editor && place === "clearing") {
      // A hold or a secondary click opens the clearing's menu for the tile, by the finger, and is
      // felt as a banner planted is.
      if (banner) {
        const d = view.tileToDevice(tile.x, tile.y);
        const at = pressAt ?? { x: d.x / devicePixelRatio, y: d.y / devicePixelRatio };
        if (editor.hold(tile, at)) vibrate(12);
      } else editor.tap(tile, pressPoint ?? undefined);
      return;
    }
    if (gallery) {
      const name = gallery.nameAt(tile);
      if (name) ui.showLabel(name);
      return;
    }
    if (lesson) {
      if (banner) vibrate(12);
      lesson.act(tile, banner);
      return;
    }
    if (!controller.canAct) {
      if (controller.together && controller.game.status === "playing") inspect(tile);
      return;
    }
    if (banner) {
      const had = controller.game.hasBanner(tile.x, tile.y);
      controller.toggleBanner(tile.x, tile.y);
      vibrate(12);
      if (!had && controller.game.hasBanner(tile.x, tile.y)) ui.showNote(tr("note.banner"));
    } else {
      const before = controller.game.record.actions.length;
      controller.tap(tile.x, tile.y);
      if (controller.game.record.actions.length > before) {
        ui.hideHint();
        if (!settings.hintSeen) {
          settings.hintSeen = true;
          saveSettings(settings);
        }
      } else inspect(tile);
    }
  }

  /**
   * A tap that left the run as it was says what it touched, in a field note with its sound, and
   * does nothing else (decision 0023). From the keyboard, the tile says it.
   */
  function inspect(tile: Coord): void {
    const game = controller.game;
    const now = harness.frozenTime ?? performance.now();
    const thing = pressPixel
      ? view.thingAt(game, pressPixel.x, pressPixel.y, now)
      : view.thingOn(game, tile.x, tile.y, now);
    if (!thing || thing.creature) return;
    const saying = sayingOf(thing.model, thing.x, thing.y, game.seed);
    if (!saying) return;
    const { key, name, tap } = saying;
    ui.showNote(tr(key as StringKey, name ? { name } : {}), SAYING_MS);
    audio?.act(`effects.${tap}` as SoundId);
  }

  /**
   * After the end, a tap on a creature standing where it hid says which it was, with its own
   * sound, in place of what the tap would do. Whether it touched one.
   */
  function sayCreature(devX: number, devY: number): boolean {
    const game = controller.game;
    if (game.status !== "dead" || ui.currentScreen === "start") return false;
    const thing = view.thingAt(game, devX, devY, harness.frozenTime ?? performance.now());
    if (!thing?.creature) return false;
    ui.showNote(tr(creatureSaying(thing.model) as StringKey), SAYING_MS);
    const sound = creatureSound(creatureKind(thing.model));
    if (sound) audio?.act(sound);
    return true;
  }

  /**
   * The compass follows the camera's turn: north is up in the default view, and as the camera
   * turns clockwise the needle turns with the world, so it always points to the world's north.
   */
  let lastCompass: number | null = null;
  function updateInstruments(): void {
    const degrees = ((view.yaw - NORTH) * 180) / Math.PI;
    if (lastCompass === null || Math.abs(degrees - lastCompass) > 0.05) {
      lastCompass = degrees;
      ui.setCompass(degrees, false);
    }
  }

  attachPointer(canvas, () => devicePixelRatio, {
    // Banner mode is the expedition's; in the clearing a tap is a tap. After a death, a tap on a
    // creature says which it was (decision 0023) and a tap elsewhere on the world shows the journey
    // again (decision 0031).
    tap: (x, y) => {
      if (lesson) {
        press(x, y, settings.bannerMode);
        return;
      }
      if (place === "run" && sayCreature(x, y)) return;
      if (!journey?.again()) press(x, y, settings.bannerMode && place === "run");
    },
    hold: (x, y) => press(x, y, true),
    secondary: (x, y) => press(x, y, true),
    stopMotion: () => {
      if (following !== null) follow(null);
      const moving = stopCamera();
      return (journey?.interrupt() ?? false) || moving;
    },
    panStart: () => {
      editor?.dismissMenu();
      panning = true;
    },
    pan: (dx, dy) => {
      view.panBy(dx, dy);
      needsFrame = true;
    },
    panEnd: () => {
      panning = false;
      view.settle();
      needsFrame = true;
    },
    pinch: (factor, x, y) => {
      editor?.dismissMenu();
      view.zoomAround(view.scale * factor, x, y);
      needsFrame = true;
    },
    twist: (radians, x, y) => {
      view.rotateBy(radians, x, y);
      needsFrame = true;
    },
    tilt: (devDy) => {
      view.tiltBy(devDy * 0.004);
      needsFrame = true;
    },
    // Two fingers became one, which drags on and glides when it lifts, or a trackpad's gesture
    // ended.
    pinchEnd: () => {
      rememberScale();
      needsFrame = true;
    },
    orbit: (dx, dy) => {
      // Grab the world: the near edge follows the mouse sideways, up tilts toward the horizon,
      // as two fingers do on a phone.
      const css = devicePixelRatio;
      view.rotateBy((-dx / css) * 0.008, view.viewport.w / 2, view.viewport.h / 2);
      view.tiltBy((dy / css) * 0.006);
      needsFrame = true;
    },
    orbitEnd: () => {
      view.settle();
      needsFrame = true;
    },
    wheel: (deltaY, x, y, pinch) => {
      // Continuous, so a trackpad's many small scrolls zoom smoothly and a mouse notch still
      // zooms by about a fifth.
      view.zoomAround(view.scale * Math.exp(-deltaY * (pinch ? 0.01 : 0.002)), x, y);
      scheduleRememberScale();
      needsFrame = true;
    },
    activity: () => {
      if (cursorVisible) {
        cursorVisible = false;
        view.invalidate();
        needsFrame = true;
      }
    },
  });

  attachKeyboard({
    move: (dx, dy) => {
      if (ui.dialogOpen) return;
      const base = cursor ?? view.tileAt(view.viewport.w / 2, view.viewport.h / 2);
      cursor = { x: base.x + dx, y: base.y + dy };
      cursorVisible = true;
      const dev = view.tileToDevice(cursor.x, cursor.y);
      const margin = 48 * view.scale;
      if (
        dev.x < margin ||
        dev.y < margin ||
        dev.x > view.viewport.w - margin ||
        dev.y > view.viewport.h - margin
      ) {
        view.centreOnTile(cursor.x, cursor.y);
      }
      view.invalidate();
      needsFrame = true;
    },
    reveal: () => {
      if (ui.dialogOpen || !cursor) return;
      const d = view.tileToDevice(cursor.x, cursor.y);
      if (tooSmallToTap()) zoomToRead(d.x, d.y);
      else act(cursor, false);
    },
    banner: () => {
      if (ui.dialogOpen || !cursor) return;
      const d = view.tileToDevice(cursor.x, cursor.y);
      if (tooSmallToTap()) zoomToRead(d.x, d.y);
      else act(cursor, true);
    },
    home: () => {
      if (ui.dialogOpen) return;
      goHome();
      ui.showNote(tr("note.cabin"));
    },
    zoom: (step) => zoomStep(step, view.viewport.w / 2, view.viewport.h / 2),
    turn: (step) => turnBy((step * Math.PI) / 4),
    tilt: (step) => {
      view.tiltBy(step * 0.12);
      needsFrame = true;
    },
    escape: () => {
      // The menu and dialogs first, as they stand over the clearing too.
      if (!ui.escape() && place === "clearing") editor?.escape();
    },
  });

  function resize(): void {
    const dpr = devicePixelRatio;
    const centre = view.tileAt(view.viewport.w / 2, view.viewport.h / 2);
    view.resize(innerWidth * dpr, innerHeight * dpr);
    canvas.style.width = `${innerWidth}px`;
    canvas.style.height = `${innerHeight}px`;
    view.centreOnTile(centre.x, centre.y);
    needsFrame = true;
  }
  addEventListener("resize", resize);
  darkQuery.addEventListener("change", () => {
    if (settings.theme === null) {
      themeChanged();
      // Settings marks the theme the system now gives.
      if (ui.menuOpen) ui.renderPage();
    }
  });

  /** The world and the interface drawn again in the theme now in use, and what follows it. */
  function themeChanged(): void {
    view.theme = currentTheme();
    applyTheme(currentTheme());
    if (ui.currentScreen === "end" && lastDeath) showResult(lastDeath);
    // The tray's pictures in the new theme's paper and ink.
    editor?.reloadPictures();
    needsFrame = true;
  }

  resize();
  const worldOnly = gallery !== null || portrait !== null;
  /* ---------- Expeditions together (decision 0043) ---------- */

  let connection: PartyConnection | null = null;
  let partyMembers: readonly MemberView[] = [];
  /** Where each other member's camera is, as they last said. */
  const looks = new Map<number, CameraView>();
  /** The member whose camera this one follows, or null. */
  let following: number | null = null;
  let lastLookSent = 0;
  let lastLookKey = "";
  const partyBanner = harness.banner ?? "1";
  const partyUi = new PartyUi(tr, {
    invite: () => void invite(),
    follow: (m) => follow(m),
    share: () => void shareLink(),
    leave: () => leaveParty(),
    join: (name) => joinWith(name),
    dismissJoin: () => forgetLink(),
    remove: (m) => {
      if (!confirm(tr("party.removeAsk", { name: memberName(m) }))) return;
      connection?.remove(m);
    },
    lock: (on) => {
      connection?.lock(on);
      ui.showNote(tr(on ? "party.lockedNote" : "party.openNote"));
    },
  });
  let linkCode: string | null = null;
  /** The host closed the party to new members. */
  let partyLocked = false;

  function mountParty(): void {
    const layer = document.getElementById("ui");
    if (!layer) return;
    partyUi.mount(layer);
    layer.classList.toggle("in-party", controller.together);
  }

  function memberName(m: number): string {
    const v = partyMembers.find((x) => x.m === m);
    return v?.name ? v.name : tr("party.guest", { n: m + 1 });
  }

  /** Each member's tiles, worked out again only when the run has moved. */
  let tallied: { game: Game; actions: number; byMember: ReturnType<typeof memberTiles> } | null =
    null;
  function memberTally(): ReturnType<typeof memberTiles> {
    const game = controller.game;
    if (tallied?.game !== game || tallied.actions !== game.actions.length) {
      tallied = { game, actions: game.actions.length, byMember: memberTiles(game.record) };
    }
    return tallied.byMember;
  }

  function refreshParty(): void {
    mountParty();
    const together = controller.together;
    const invite =
      !together &&
      place === "run" &&
      controller.mode === "endless" &&
      controller.game.status === "playing" &&
      gallery === null &&
      portrait === null;
    document.getElementById("ui")?.classList.toggle("has-invite", invite);
    if (!together) {
      const name = ownName();
      const me = {
        m: 0,
        name,
        you: true,
        status: "out" as const,
        followed: false,
        tiles: 0,
        distance: "",
        named: name !== "",
      };
      partyUi.setMembers(invite ? [me] : null, invite, true);
      partyUi.setFollowing(null);
      partyUi.setLanterns([]);
      return;
    }
    const game = controller.game;
    const you = controller.party?.you ?? 0;
    // Who the host took out stays in the record, not in the row.
    const chips = partyMembers
      .filter((v) => !v.removed)
      .map((v) => ({
        m: v.m,
        name: memberName(v.m),
        you: v.m === you,
        status: game.hasFallen(v.m)
          ? ("fell" as const)
          : v.online
            ? ("out" as const)
            : ("away" as const),
        followed: following === v.m,
        tiles: memberTally().get(v.m)?.tiles ?? 0,
        distance: fmt(memberTally().get(v.m)?.metres ?? 0),
        named: v.name !== "",
      }));
    partyUi.setMembers(
      chips,
      partyMembers.length < PARTY_LIMIT &&
        game.status === "playing" &&
        !controller.watching &&
        !partyLocked,
    );
    partyUi.setFollowing(following === null ? null : memberName(following));
    partyUi.setHost((controller.party?.you ?? -1) === 0 && !controller.watching, partyLocked);
  }

  /** The nearest member still out in the mist with a camera to follow, from a tile; or null. */
  function nearestOut(from: Coord): number | null {
    const game = controller.game;
    let best: number | null = null;
    let bestD = Infinity;
    for (const v of partyMembers) {
      if (v.m === controller.party?.you || !v.online || game.hasFallen(v.m)) continue;
      const look = looks.get(v.m);
      const d = look ? (look.x - from.x) ** 2 + (look.y - from.y) ** 2 : 1e12;
      if (d < bestD) {
        bestD = d;
        best = v.m;
      }
    }
    return best;
  }

  /** Follows a member's camera, as one follows another in Figma, or lets go with null. */
  function follow(m: number | null): void {
    following = m === controller.party?.you ? null : m;
    if (following !== null) stopCamera();
    refreshParty();
    needsFrame = true;
  }

  function dropParty(): void {
    connection?.close();
    connection = null;
    controller.sendAction = null;
    partyMembers = [];
    looks.clear();
    following = null;
    refreshParty();
  }

  /** The angle from a to b the short way round. */
  function turn(a: number, b: number): number {
    return Math.atan2(Math.sin(b - a), Math.cos(b - a));
  }

  /**
   * Every frame in a party: the camera glides after the one followed, this camera is told to the
   * others five times a second when it moved, and the others' lanterns stand where they look.
   */
  function partyFrame(now: number): void {
    if (!controller.together || place !== "run") {
      partyUi.setLanterns([]);
      return;
    }
    if (following !== null) {
      const target = looks.get(following);
      if (target) {
        const cur = view.cameraView();
        const k = reducedMotion ? 1 : 0.14;
        const next = {
          x: cur.x + (target.x - cur.x) * k,
          y: cur.y + (target.y - cur.y) * k,
          yaw: cur.yaw + turn(cur.yaw, target.yaw) * k,
          pitch: cur.pitch + (target.pitch - cur.pitch) * k,
          scale: cur.scale * (target.scale / cur.scale) ** k,
        };
        const far =
          Math.abs(target.x - cur.x) + Math.abs(target.y - cur.y) > 0.01 ||
          Math.abs(turn(cur.yaw, target.yaw)) > 0.002 ||
          Math.abs(target.scale / cur.scale - 1) > 0.002;
        if (far) {
          view.setCameraView(next);
          needsFrame = true;
        }
      }
    }
    if (now - lastLookSent > 200 && !controller.watching) {
      const c = view.cameraView();
      const look = [c.x, c.y, c.yaw, c.pitch, c.scale];
      const key = look.map((n) => n.toFixed(2)).join(",");
      if (key !== lastLookKey) {
        lastLookKey = key;
        lastLookSent = now;
        connection?.look(look);
      }
    }
    const dpr = devicePixelRatio;
    const w = view.viewport.w;
    const h = view.viewport.h;
    const you = controller.party?.you ?? 0;
    const game = controller.game;
    const lanterns = partyMembers
      .filter((v) => v.m !== you && v.online && looks.has(v.m))
      .map((v) => {
        const look = looks.get(v.m) as CameraView;
        const d = view.pointToDevice(look.x, look.y, 0.4);
        const inside = d.x >= 0 && d.y >= 0 && d.x <= w && d.y <= h;
        return {
          m: v.m,
          name: memberName(v.m),
          at: inside ? { x: d.x / dpr, y: d.y / dpr } : null,
          fell: game.hasFallen(v.m),
        };
      });
    partyUi.setLanterns(lanterns);
  }

  /** The line to the party's room, saying hello with the token when there is one. */
  function connect(code: string, token: string | undefined, name: string, watch?: string): void {
    connection?.close();
    connection = new PartyConnection(
      code,
      { ...(token ? { token } : {}), name, banner: partyBanner, ...(watch ? { watch } : {}) },
      {
        welcome: (you, tok, record, list, locked) => {
          partyMembers = list;
          partyLocked = locked;
          if (controller.party?.code !== code) {
            if (!watch) {
              settings.mode = "endless";
              saveSettings(settings);
            }
            controller.joinParty({ v: 1, code, token: tok, you, record });
            play();
          } else controller.partyWelcome(you, tok, record);
          controller.partySize(list.length);
          refreshParty();
          needsFrame = true;
        },
        act: (a) => controller.partyAction(a),
        members: (list, locked) => {
          const before = new Set(partyMembers.map((m) => m.m));
          const removedBefore = new Set(partyMembers.filter((m) => m.removed).map((m) => m.m));
          partyMembers = list;
          partyLocked = locked;
          for (const m of list) {
            if (m.removed && !removedBefore.has(m.m) && before.has(m.m) && controller.together) {
              ui.showNote(tr("party.memberRemoved", { name: memberName(m.m) }));
            }
          }
          controller.partySize(list.length);
          for (const m of list) {
            if (!before.has(m.m) && m.m !== controller.party?.you && controller.together) {
              ui.showNote(tr("party.joined", { name: memberName(m.m) }));
            }
            if (!m.online) looks.delete(m.m);
          }
          if (following !== null && !list.find((m) => m.m === following)?.online) following = null;
          refreshParty();
          needsFrame = true;
        },
        look: (m, c) => {
          const [x, y, yaw, pitch, scale] = c;
          if (scale === undefined || x === undefined || y === undefined) return;
          looks.set(m, { x, y, yaw: yaw ?? 0, pitch: pitch ?? 0, scale });
          needsFrame = true;
        },
        refused: (why) => {
          connection = null;
          controller.sendAction = null;
          const key =
            why === "full"
              ? "party.full"
              : why === "over" || why === "gone"
                ? "party.over"
                : why === "removed"
                  ? "party.removed"
                  : why === "closed"
                    ? "party.locked"
                    : "party.unavailable";
          ui.showNote(tr(key), SAYING_MS);
          // A party deleted after its week, or one the host took this player out of: the run kept
          // here leaves with it, and the player is alone again.
          if ((why === "gone" || why === "removed") && controller.party) {
            const shown = controller.together;
            dropParty();
            controller.leaveParty(Date.now());
            if (shown) play();
          }
          refreshParty();
        },
        line: (up) => {
          if (controller.together) ui.showNote(tr(up ? "party.back" : "party.lost"));
        },
      },
    );
    controller.sendAction = (a) => connection?.act(a[0], a[1], a[2]);
  }

  /** The player's name for a party: the one on the lists, if any. */
  function ownName(): string {
    return todays.settings.name ?? "";
  }

  /** Invite: the run under way becomes a party's, and the sheet with its link opens. */
  async function invite(): Promise<void> {
    if (controller.together) {
      showInviteSheet();
      return;
    }
    if (controller.mode !== "endless" || controller.game.status !== "playing") return;
    const record = controller.game.record;
    const started = await startParty(record, ownName(), partyBanner);
    if ("error" in started) {
      ui.showNote(tr("party.unavailable"));
      return;
    }
    controller.joinParty({ v: 1, code: started.code, token: started.token, you: 0, record }, true);
    play();
    connect(started.code, started.token, ownName());
    showInviteSheet();
  }

  function showInviteSheet(): void {
    const code = controller.party?.code;
    if (!code) return;
    partyUi.showInvite(tr("party.inviteBody", { n: PARTY_LIMIT }), partyLink(code), true);
  }

  async function shareLink(): Promise<void> {
    const code = controller.party?.code;
    if (!code) return;
    const url = partyLink(code);
    const text = tr("party.shareText");
    if (navigator.share) {
      try {
        await navigator.share({ text, url });
        return;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      ui.showNote(tr("party.copied"));
    } catch {
      // The link stands in the sheet to copy by hand.
    }
  }

  function leaveParty(): void {
    dropParty();
    controller.leaveParty(Date.now());
    play();
  }

  /** A link to a party: what it holds, then the sheet to join, or a note why one cannot. */
  async function openLink(code: string): Promise<void> {
    const saved = loadTogether();
    if (saved?.code === code) {
      forgetLink();
      return;
    }
    const info = await partyInfo(code);
    if (!info || info.over || info.locked || info.members >= info.limit) {
      ui.showNote(
        tr(!info || info.over ? "party.over" : info.locked ? "party.locked" : "party.full"),
      );
      forgetLink();
      return;
    }
    linkCode = code;
    const title = info.host
      ? tr("party.joinTitle", { name: info.host })
      : tr("party.joinTitleAnon");
    partyUi.showJoin(title, tr("party.joinBody", { distance: fmt(info.metres) }), ownName());
  }

  function joinWith(raw: string): void {
    const code = linkCode;
    forgetLink();
    if (!code) return;
    const name = normaliseName(raw);
    connect(code, undefined, name === "" || nameFits(name) ? name : "");
  }

  /**
   * Daniel's page sends him here to watch a party (decision 0043), with his key from that page:
   * live, as a member nobody sees, or played again from its record, an action at a time, the
   * camera going where each was taken.
   */
  async function watchParty(code: string, replay: boolean): Promise<void> {
    const key = getJson<string>("admin.key");
    if (!key) {
      ui.showNote(tr("party.unavailable"));
      return;
    }
    if (!replay) {
      connect(code, undefined, "", key);
      return;
    }
    let got: { record?: unknown; members?: { m: number; name: string; banner: string }[] };
    try {
      const response = await fetch("/api/v1/admin/party", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ v: 1, key, code }),
      });
      got = response.ok ? ((await response.json()) as typeof got) : {};
    } catch {
      got = {};
    }
    let record: RunRecord;
    try {
      record = parseRunRecord(got.record);
    } catch {
      ui.showNote(tr("party.over"));
      return;
    }
    partyMembers = (got.members ?? []).map((m) => ({ ...m, online: true }));
    controller.joinParty({ v: 1, code, token: "", you: -1, record: { ...record, actions: [] } });
    controller.partySize(record.party ?? 1);
    play();
    const actions = record.actions;
    let i = 0;
    const step = () => {
      const action = actions[i];
      if (!action || controller.party?.code !== code) return;
      i += 1;
      controller.partyAction(action);
      glideFocus({ x: action[1], y: action[2] }, reducedMotion ? 0 : 300);
      setTimeout(step, 350);
    };
    setTimeout(step, 800);
  }

  /** The address goes back to the game's own, so a reload opens the game, not the link. */
  function forgetLink(): void {
    linkCode = null;
    if (codeInPath(location.pathname)) history.replaceState(null, "", `/${location.search}`);
  }

  // The player's own cabin in the cabin's place (decision 0029), in the game's own world only.
  if (!worldOnly) view.setHome(settings.home);
  if (harness.seed !== null) {
    settings.mode = "endless";
    controller.startEndlessWithSeed(harness.seed, Date.now());
  } else {
    controller.start(settings.mode, Date.now());
    // A party's run kept here waits under Endless and takes its place there again (decision 0043).
    const together = worldOnly || harness.watch ? null : loadTogether();
    if (together) {
      controller.resumeParty(together);
      connect(together.code, together.token, ownName());
    }
  }
  // The place that shows keeps its view as the page goes away.
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) keepView();
    // Back on the page, what should be on today's list and is not is sent.
    else if (todays.enabled) void todays.catchUp(Date.now(), loadDailyResult);
  });
  if (todays.enabled) {
    void todays.catchUp(Date.now(), loadDailyResult);
    addEventListener("online", () => void todays.catchUp(Date.now(), loadDailyResult));
  }
  if (worldOnly) {
    ui.hideAll();
    ui.setScreen("play");
  } else if (harness.clearing) {
    // `?clearing`: the game opens in the clearing, the run waiting under Today and Endless.
    play();
    enterClearing();
  } else if (harness.seed !== null) {
    // A seeded run is for tests and reference frames: straight into play.
    play();
  } else {
    refreshHud();
    ui.showStart(startState());
  }
  // A link to a party opens its sheet over whatever shows (decision 0043); a visitor who has
  // never played takes the lesson first, and the sheet follows it (decision 0045).
  const linked = worldOnly ? null : codeInPath(location.pathname);
  const opensLesson =
    !worldOnly && !harness.clearing && harness.seed === null && !harness.watch && lessonFirst();
  if (opensLesson) {
    startLesson(linked ? "invite" : "first", linked ? () => void openLink(linked) : null);
  } else if (linked) void openLink(linked);
  else if (harness.watch && !worldOnly) void watchParty(harness.watch, harness.replay);
  if (pick && !worldOnly) {
    showFound();
    // Runs from before finds were noted as they show are read once, when the game is at rest.
    const finder = pick;
    setTimeout(() => {
      if (!collected) collectFinds(savedRecords(), finder);
      collected = true;
      showFound();
      editor?.setEntries(catalog(modelNames, loadFound(), harness.all));
    }, 1500);
  }

  /** The mist lifts off a place's ground as the player comes to it. */
  function liftMist(tiles: readonly Coord[]): void {
    const at = harness.frozenTime === null ? performance.now() : harness.frozenTime - FADE_MS;
    view.fog.markRevealed(tiles, at);
    view.invalidate();
  }

  /** The clearing's size and photo mode, as the bars show them. */
  function showTools(): void {
    if (clearing) ui.setClearingTools(sizeOf(clearing), editor?.photographing ?? false);
  }

  /**
   * Into the clearing, in place, as Today and Endless are chosen (Daniel, 2026-10-19): the same
   * bars, the clearing's world in the run's, and its controls between them. The run waits as it
   * is, and so does the camera's place on it. False when the clearing does not open.
   */
  function enterClearing(): boolean {
    const finder = pick;
    if (place === "clearing" || !finder || gallery || portrait) return false;
    // A run that has just ended shows its result first.
    if (resultTimer !== null) return false;
    if (!collected) {
      collectFinds(savedRecords(), finder);
      collected = true;
      showFound();
    }
    setTurnTrial(harness.turn);
    clearings ??= loadClearings(new Set(modelNames));
    clearing = chosenOf(clearings).state;
    if (!clearingSource) {
      const current = () => clearing as ClearingState;
      const world = clearingWorld(current);
      clearingSource = { world, dress: clearingDress(current, world, finder) };
    }
    const tools = clearingEditor();
    if (!tools) return false;
    tools.setEntries(catalog(modelNames, loadFound(), harness.all));
    // The run keeps its view, as the camera stood before any journey, and the clearing takes its
    // own.
    journey?.leave();
    keepView();
    place = "clearing";
    // The wild animals come to the clearing as they do on a run (Daniel, 2026-10-19).
    view.setDress(clearingSource.dress, true);
    view.startRun();
    showClearingView(clearings.chosen);
    view.settle();
    liftMist(groundOf(clearing));
    cursorVisible = false;
    tools.show();
    showClearings();
    ui.setPlace("clearing");
    showTools();
    needsFrame = true;
    return true;
  }

  /** The tiles of a clearing's ground, within its reach. */
  function groundOf(state: ClearingState): Coord[] {
    const r = state.reach;
    const ground: Coord[] = [];
    for (let y = -r; y <= r; y += 1) for (let x = -r; x <= r; x += 1) ground.push({ x, y });
    return ground;
  }

  /** The player's clearings, in the row under the top bar. */
  function showClearings(): void {
    if (!clearings) return;
    ui.setClearings(
      clearings.list.map(({ id, name }) => ({ id, name })),
      clearings.chosen,
    );
  }

  /**
   * Another of the player's clearings shows, chosen in the row, made with its plus, or in place of
   * one deleted: its ground rises out of the mist where the last one stood, as the clearing's does
   * when it opens, with the camera where it was left in it.
   */
  function switchClearing(next: Clearings): void {
    if (!clearings || !clearingSource) return;
    keepView();
    clearings = next;
    saveClearings(next);
    const shown = chosenOf(next);
    clearing = shown.state;
    editor?.switchTo(shown.id, shown.state);
    editTile = null;
    view.setDress(clearingSource.dress, true);
    view.startRun();
    showClearingView(shown.id);
    view.settle();
    liftMist(groundOf(shown.state));
    showClearings();
    showTools();
    needsFrame = true;
  }

  /** Back to the expedition: the run's world, where the camera left it. */
  function leaveClearing(): void {
    if (place !== "clearing") return;
    editor?.hide();
    editTile = null;
    keepView();
    place = "run";
    view.setDress(null);
    view.startRun();
    showRunView();
    view.settle();
    const at = view.focus();
    liftMist(
      controller.game
        .revealedTiles()
        .filter((t) => Math.abs(t.x - at.x) <= LIFT_REACH && Math.abs(t.y - at.y) <= LIFT_REACH),
    );
    cursorVisible = false;
    ui.setPlace("run");
    needsFrame = true;
  }

  /**
   * Keeps where the camera looks in the place that shows, in the clearing under its id and on a
   * run under its mode with the run's name, and writes the views down. The gallery and a
   * portrait keep none.
   */
  function keepView(): void {
    if (place === "clearing") {
      if (!clearings) return;
      views = withClearingView(views, clearings.chosen, view.cameraView());
    } else {
      // The lesson's camera is the lesson's: the run keeps the view it had.
      if (!shownRun || lesson) return;
      // A journey's view from above is the journey's, not the player's.
      const camera = journey?.keptView ?? view.cameraView();
      views = withRunView(views, shownRun.mode, shownRun.name, camera);
    }
    if (keepsViews) saveViews(views);
  }

  /**
   * The run that shows, as the camera left it; a run not seen before opens on the cabin, turned,
   * tilted and zoomed as its mode was last seen, or as the camera stands. The gallery and a
   * portrait look at their own centre.
   */
  function showRunView(): void {
    const game = controller.game;
    const kept = views.runs[game.mode];
    if (gallery || portrait) {
      shownRun = null;
      view.centreOnTile((gallery?.centre ?? home).x, (gallery?.centre ?? home).y);
    } else {
      shownRun = { mode: game.mode, name: runName(game) };
      if (kept?.run === shownRun.name) view.setCameraView(kept);
      else if (kept) view.setCameraView({ ...kept, x: home.x, y: home.y });
      else view.centreOnTile(home.x, home.y);
    }
  }

  /** A clearing that shows, as the camera left it there, or on the cabin as the camera stands. */
  function showClearingView(id: number): void {
    const kept = views.clearings[String(id)];
    if (kept) view.setCameraView(kept);
    else view.setFocus({ x: 0, y: 0 });
  }

  /** The clearing's controls, made the first time it shows and again after a change of language. */
  function clearingEditor(): ClearingEditor | null {
    if (editor) return editor;
    if (!clearing || !clearings) return null;
    editor = new ClearingEditor(
      clearingHost,
      locale,
      clearings.chosen,
      clearing,
      catalog(modelNames, loadFound(), harness.all),
      {
        changed: (next, shown) => {
          clearing = next;
          if (clearings) {
            clearings = updateClearing(clearings, clearings.chosen, next);
            saveClearings(clearings);
          }
          if (shown.length > 0) liftMist(shown);
          view.invalidate();
          showTools();
          needsFrame = true;
        },
        note: (text) => ui.showNote(text),
        photo: () => {
          showTools();
          needsFrame = true;
        },
        thumbnail: (key) => thumbnails()?.request(key) ?? Promise.resolve(null),
        prioritize: (keys) => thumbnails()?.prioritize(keys),
        mark: (tile) => {
          editTile = tile;
          needsFrame = true;
        },
        home: (name) => {
          settings.home = name;
          saveSettings(settings);
          view.setHome(name);
          needsFrame = true;
        },
        cabinHintSeen: () => {
          settings.homeHintSeen = true;
          saveSettings(settings);
        },
        capture: (rect) => {
          // Drawn and read in one go: the canvas keeps its picture only until the frame is shown.
          const dpr = devicePixelRatio;
          const w = Math.round(rect.width * dpr);
          const h = Math.round(rect.height * dpr);
          const out = document.createElement("canvas");
          out.width = w;
          out.height = h;
          const ctx = out.getContext("2d");
          if (!ctx || w < 1 || h < 1) return null;
          view.render(
            clearingSource?.world ?? controller.game,
            harness.frozenTime ?? performance.now(),
            null,
          );
          ctx.drawImage(
            canvas,
            Math.round(rect.left * dpr),
            Math.round(rect.top * dpr),
            w,
            h,
            0,
            0,
            w,
            h,
          );
          needsFrame = true;
          return out;
        },
        sound: (what) => {
          if (!audio) return;
          if (what.kind === "place") {
            audio.act(placeSound(categoryOf(what.name), familyOf(what.name)));
          } else if (what.kind === "ground") audio.act(revealSound(terrainOf(what.ground)));
          else audio.act(CLEARING_SOUNDS[what.kind]);
        },
      },
    );
    editor.setHome(settings.home);
    return editor;
  }

  /**
   * The note the clearing opens with, what the clearing is for; and until the player has closed
   * it, after a house that can be the player's cabin has been found, the hint over the tray that
   * the cabin can be any of them, the cabin marked a moment (decision 0029).
   */
  function clearingNote(): string {
    const choices = homeChoices(catalog(modelNames, loadFound(), harness.all));
    if (settings.homeHintSeen || choices.length === 0 || !editor) return tr("note.clearing");
    editor.showCabinHint();
    const mark = { x: 0, y: 0 };
    editTile = mark;
    needsFrame = true;
    setTimeout(() => {
      if (editTile !== mark) return;
      editTile = null;
      needsFrame = true;
    }, CABIN_MARK_MS);
    return tr("note.clearing");
  }

  if (harness.capture) {
    (window as unknown as { __mistsweeper: unknown }).__mistsweeper = {
      controller,
      view,
      gallery,
      clearing: () => editor?.current ?? null,
      clearings: () =>
        clearings && {
          chosen: clearings.chosen,
          list: clearings.list.map(({ id, name, state }) => ({ id, name, reach: state.reach })),
        },
      get editor() {
        return editor;
      },
      place: () => place,
      /** Expeditions together (decision 0043): the party as this browser sees it. */
      party: () =>
        controller.party && {
          code: controller.party.code,
          you: controller.party.you,
          together: controller.together,
          size: controller.game.party,
          falls: controller.game.falls.map((f) => [f.member, f.x, f.y]),
          members: partyMembers,
          following,
          online: connection?.online ?? false,
          looks: [...looks.keys()],
        },
      follow,
      /** Expeditions together: Invite, as the button does. */
      invite: () => invite(),
      marked: () => editTile,
      settings,
      ui,
      act,
      journey,
      /** The lesson of decision 0045, while it shows. */
      lesson: () => lesson,
      refresh: () => {
        needsFrame = true;
      },
      /**
       * The films of apps/web/scripts: what the game plays goes to `sink` instead, an object with
       * GameAudio's methods, and `listen(now)` hears the screen as the game does five times a
       * second, `now` in seconds of the renderer's clock.
       */
      hear: (sink: GameAudio | null) => {
        audio = sink;
      },
      listen: (now: number) => listen(now),
      /** The world the renderer draws, for a film that draws it itself. */
      world: () => shownWorld(),
      /**
       * The theme lab (decision 0034): shows a theme in the world and the interface without
       * saving it, or the player's own again with null.
       */
      previewTheme: (theme: ThemeDef | null) => {
        const was = themeId();
        previewTheme = theme;
        view.theme = currentTheme();
        applyTheme(currentTheme());
        // The sheet's portrait, Settings' Day and Night and the tray's pictures follow Day or
        // Night: drawn again only when that changes, not at every step of a colour being dragged.
        if (themeId() !== was) {
          if (ui.currentScreen === "end" && lastDeath) showResult(lastDeath);
          if (ui.menuOpen) ui.renderPage();
          editor?.reloadPictures();
        }
        needsFrame = true;
      },
    };
  }

  setInterval(() => controller.tick(Date.now()), 1000);

  // The sound of the world (decision 0022): five times a second the screen is read for the
  // ambience, and what lives there is heard as it moves, on the renderer's clock.
  const called = new Map<string, number>();
  const sounding = new Map<string, Sounding | null>();

  /** Hears the screen at `now`, in seconds of the renderer's clock: the present one by default. */
  function listen(now = (harness.frozenTime ?? performance.now()) / 1000): void {
    const audio = playing();
    if (!audio) return;
    const scene = view.lastScene;
    if (!scene || gallery !== null || portrait !== null) {
      audio.setAmbience({});
      return;
    }
    const { w, h } = view.viewport;
    const counts = [0, 0, 0, 0];
    for (let j = 0; j < SAMPLE_ROWS; j += 1) {
      for (let i = 0; i < SAMPLE_COLUMNS; i += 1) {
        const tile = view.tileAt(((i + 0.5) / SAMPLE_COLUMNS) * w, ((j + 0.5) / SAMPLE_ROWS) * h);
        const ground = shownTerrain(scene, tile.x, tile.y);
        if (ground !== null) counts[ground] = (counts[ground] ?? 0) + 1;
      }
    }
    const share = (t: Terrain) => (counts[t] ?? 0) / (SAMPLE_COLUMNS * SAMPLE_ROWS);
    const left = view.pointAt(0, h / 2);
    const right = view.pointAt(w, h / 2);
    const near = nearness(Math.hypot(right.x - left.x, right.y - left.y));
    const shares = {
      water: share(Terrain.water),
      grass: share(Terrain.grass),
      forest: share(Terrain.forest),
      mountain: share(Terrain.mountain),
    };
    const levels: Record<string, number> = { ...bedLevels(shares, near, themeId() === "night") };
    const louder = (id: SoundId, level: number) => {
      levels[id] = Math.max(levels[id] ?? 0, level);
    };
    // How near the middle of the screen a point of the world is.
    const heard = (x: number, y: number, height: number, reach = 1) => {
      const p = view.pointToDevice(x, y, height);
      return proximity(p.x, p.y, w, h, reach);
    };
    // The cabin's fire and its pennant, at its tile.
    const home = homeLevel(heard(0, 0, 0.5), near);
    louder("ambience.chimney", home);
    louder("ambience.pennant", home);
    for (const [k, until] of called) if (until < now) called.delete(k);
    const later = now + LOOKAHEAD_S;
    // A call sounds once by its key, `lead` seconds before its moment, which is that far into
    // its file; the key is kept `hold` seconds after it.
    const call = (key: string, id: SoundId, at: number, level: number, lead = 0, hold = 5) => {
      if (level < 0.02 || called.has(key)) return;
      called.set(key, at + hold);
      audio.play(id, Math.max(0, at - lead - now), 0, level);
    };
    const life = view.living;
    // What burns or turns sounds while it shows; an eagle cries as it circles, an elk grunts as it
    // looks up.
    for (const [name, list] of scene.instances) {
      let kind = sounding.get(name);
      if (kind === undefined) {
        kind = soundingOf(name);
        sounding.set(name, kind);
      }
      if (!kind || (kind.life && !life)) continue;
      for (const it of list) {
        if (it.flags & FLAG.ghost) continue;
        if (kind.loop) louder(kind.loop, sourceLevel(heard(it.x, it.y, it.h + 0.3), near));
        else if (kind.call === "eagle") {
          const level = skyLevel(heard(it.x, it.y, it.h + 3, 1.2), near);
          for (const c of eagleCries(it.x, it.y, now, later)) {
            call(`eagle:${it.x},${it.y}:${c.round}`, "effects.eagle", c.at, level);
          }
        } else {
          const level = callLevel(heard(it.x, it.y, it.h), near);
          for (const c of elkGrunts(it.x, it.y, now, later)) {
            call(`elk:${it.x},${it.y}:${c.round}`, "effects.elk", c.at, level);
          }
        }
      }
    }
    if (life) {
      // The gnats of the swarm nearest the middle, close up.
      let gnats = 0;
      for (const g of scene.swarms) {
        if (shownTerrain(scene, g.tx, g.ty) === null) continue;
        gnats = Math.max(gnats, gnatLevel(heard(g.x, g.y, g.h, 0.6), near));
      }
      louder("ambience.gnats", gnats);
      // A fish's splash as it falls back in, its loudest moment on the fall.
      for (const f of scene.fish) {
        if (shownTerrain(scene, f.tx, f.ty) === null) continue;
        const level = callLevel(heard(f.x, f.y, f.h, 0.9), near);
        for (const c of fishFalls(f.period, f.phase, now, later)) {
          call(`fish:${f.tx},${f.ty}:${c.round}`, "effects.fish", c.at, level, FISH_SPLASH_S);
        }
      }
      // A duck calls as it looks about.
      for (const d of scene.ducks) {
        if (shownTerrain(scene, d.tx, d.ty) === null) continue;
        const level = callLevel(heard(d.x, d.y, d.h, 0.9), near);
        for (const c of duckCalls(d.x, d.y, now, later)) {
          call(`duck:${d.tx},${d.ty}:${c.round}`, "effects.duck", c.at, level);
        }
      }
      // A flock's wings, the first time it beats them near the middle of the screen, once in its
      // flight across.
      for (const f of view.flocks) {
        const q = heard(f.x, f.y, f.h);
        if (q < 0.25 || f.beat >= later) continue;
        call(`birds:${f.k}`, "effects.birds", f.beat, skyLevel(q, near), BIRDS_LEAD_S, 60);
      }
    }
    audio.setAmbience(levels);
  }

  if (audio) setInterval(() => listen(), LISTEN_MS);

  // Mist drift and smoke animate at the idle cadence; under reduced motion or a frozen test clock
  // frames are drawn only on demand.
  const continuous = () => !reducedMotion && harness.frozenTime === null;

  /** The world the renderer draws: the gallery, the clearing, a portrait, the lesson, the journey or the run. */
  function shownWorld(): WorldSource {
    return (
      gallery?.world ??
      (place === "clearing" ? clearingSource?.world : null) ??
      portrait?.world ??
      lesson?.world ??
      journey?.world ??
      controller.game
    );
  }

  function loop(now: number): void {
    requestAnimationFrame(loop);
    if (document.hidden) return;
    const idle = !needsFrame && !panning;
    if (connection) partyFrame(now);
    if (!needsFrame && idle && (!continuous() || now - lastFrame < IDLE_FRAME_MS)) return;
    needsFrame = false;
    const stats = view.render(
      shownWorld(),
      harness.frozenTime ?? now,
      editor?.photographing ? null : cursorVisible ? cursor : editTile,
    );
    updateInstruments();
    if (lesson && lessonCard) {
      lessonCard.place(
        (x, y) => {
          const d = view.pointToDevice(x, y, 0);
          return { x: d.x / devicePixelRatio, y: d.y / devicePixelRatio };
        },
        lessonTiles(lesson.index, lesson.step),
      );
    }
    if (stats.animating || stats.quick) needsFrame = true;
    if (settings.debug && harness.debug) {
      frameTimes.push(now - lastFrame);
      if (frameTimes.length > 60) frameTimes = frameTimes.slice(-60);
      const sorted = [...frameTimes].sort((a, b) => a - b);
      const p95 = sorted[Math.floor(sorted.length * 0.95)] ?? 0;
      const avg = frameTimes.reduce((a, b) => a + b, 0) / Math.max(1, frameTimes.length);
      ui.setDebug(
        [
          `frame ${avg.toFixed(1)} ms avg, ${p95.toFixed(1)} ms p95`,
          `compose ${stats.composeMs.toFixed(2)} ms, draw ${stats.drawMs.toFixed(2)} ms`,
          `scale ${view.scale.toFixed(2)}, dot ${view.dot}`,
          `viewport ${view.viewport.w} x ${view.viewport.h} @ ${devicePixelRatio.toFixed(2)}`,
          `day ${dayNumber(Date.now())}`,
        ].join("\n"),
      );
    }
    lastFrame = now;
  }
  requestAnimationFrame(loop);
}

// A page drawn for a picture or a test, as the theme lab's frames are, is no visit.
if (!new URLSearchParams(location.search).has("capture")) startAnalytics();
main().catch((error: unknown) => {
  console.error(error);
});
